/**
 * Mail-assistent — dagelijkse mail-triage, bewust mail-per-mail (focus, geen
 * lijst-afleiding). Per mail: samenvatting, prioriteit, concept-antwoord (staat
 * als draft in de Outlook-thread klaar) en actie-suggesties.
 *
 * Draft-only: versturen doe je zelf in Outlook. Elke afhandeling wordt gelogd
 * (self-learning). Zie docs/mail-assistent-plan.md.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Button, Chip, Stack, CircularProgress, Alert, Tooltip,
  ToggleButtonGroup, ToggleButton, Link,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, ListItemText, IconButton,
  useMediaQuery, Autocomplete, FormControlLabel, Checkbox, Collapse, Menu, ListItemIcon, Snackbar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { createTasksBatch as createMicrosoftTodoTasks, initMicrosoftTodoService } from '../../utils/microsoftTodoService';
import { leesJson } from '../../utils/apiFout';
import { bevestig } from '../../utils/bevestig';
import { voerUitgesteldUit } from '../../utils/uitgesteld';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DraftsIcon from '@mui/icons-material/Drafts';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddTaskIcon from '@mui/icons-material/AddTask';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import EventIcon from '@mui/icons-material/Event';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SendIcon from '@mui/icons-material/Send';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { BACKEND_URL, AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { fetchRecentInbox, fetchRecentSent, createReplyDraft, deleteDraft, getMailBody, forwardMail, searchMail } from '../../utils/graphMailSearch';
import { startActivity } from '../../utils/activityTracker';
import { buildGrippOfferUrl, buildGrippProjectUrl } from '../../utils/grippDeepLink';
import PageHeader from '../../ui-components/PageHeader';
import FritsOnboarding, { shouldShowOnboarding, markOnboardingSeen } from './FritsOnboarding';
import SignatureDialog from './SignatureDialog';
import ToneProfileDialog from './ToneProfileDialog';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import { formatDateISO } from '../../utils/weekUtils';

const PRIO_STYLE = {
  hoog: { label: 'Hoog', bg: '#fee2e2', color: '#b91c1c' },
  normaal: { label: 'Normaal', bg: '#e0e7ff', color: '#4338ca' },
  laag: { label: 'Laag', bg: '#f1f5f9', color: '#64748b' },
};
const CAT_LABEL = {
  beantwoorden: '✉️ Beantwoorden', taak: '✅ Taak', planning: '📅 Planning',
  offerte: '🧾 Offerte', fyi: '👀 FYI', nieuwsbrief: '📰 Nieuwsbrief',
};

// Tijdstippen per kwartier (07:00–20:00) — native time-picker negeert step op mobiel.
const QUARTERS = (() => {
  const out = [];
  for (let h = 7; h <= 20; h++) for (const m of [0, 15, 30, 45]) out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  return out;
})();

// Zorg dat de eindtijd ná de starttijd ligt — mail-voorstellen hebben vaak alleen een
// starttijd ("morgen om 10:00"), dan werd Van=Tot en klapte de check ("end_time moet
// na start_time"). Fallback: start + 1 uur (max 20:45).
function endAfterStart(start, end) {
  const toMin = (t) => { const [h, m] = String(t || '').split(':').map(Number); return (h || 0) * 60 + (m || 0); };
  if (end && toMin(end) > toMin(start)) return end;
  const target = Math.min(toMin(start) + 60, 20 * 60 + 45);
  const h = Math.floor(target / 60), m = target % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// Mail-preview: Graph's platte-tekst conversie laat links achter als "Tekst<https://url>"
// en afbeeldingen als "[https://...png]" — maak er nette klikbare linkjes van.
function renderMailText(text) {
  const cleaned = String(text || '')
    .replace(/\[(https?:\/\/[^\]]*\.(?:png|jpe?g|gif|webp|svg)[^\]]*)\]/gi, '')  // afbeeldings-placeholders
    .replace(/\[cid:[^\]]+\]/gi, '')                                            // inline-attachment refs
    .replace(/\n{3,}/g, '\n\n');
  const parts = cleaned.split(/(<https?:\/\/[^>\s]+>|https?:\/\/[^\s<>\])]+)/g);
  return parts.map((part, i) => {
    let url = null;
    if (/^<https?:\/\//.test(part)) url = part.slice(1, -1);
    else if (/^https?:\/\//.test(part)) url = part;
    if (!url) return part;
    let label = 'link';
    try { label = new URL(url).hostname.replace(/^www\./, ''); } catch { /* hou 'link' */ }
    return (
      <Link key={i} href={url} target="_blank" rel="noopener noreferrer"
        sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
        {label}&nbsp;↗
      </Link>
    );
  });
}

const CHAT_SUGGESTIONS = ['Is hier al op gereageerd?', 'Maak een korte reminder-mail', 'Pas het concept aan: korter en informeler'];

// 💬 Inline chat met Frits — als losse module-level component met LOKALE input-state.
// Zo re-rendert typen alléén dit blok, niet de hele mail-lijst (dat gaf de "springt
// in beeld / voelt vast"-jank omdat elke toetsaanslag de paginastate bijwerkte).
const InlineChat = React.memo(function InlineChat({ item, messages, busy, onSend, onApplyConcept, inputRef }) {
  const [input, setInput] = useState('');
  const send = (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput('');
    onSend(item, content);
  };
  return (
    <Box sx={{ mt: 2 }}>
      {messages.length > 0 && (
        <Stack spacing={0.75} sx={{
          mb: 1, maxHeight: 260, overflowY: 'auto', pr: 0.5,
          // Zonder vaste gutter knipperde de scrollbar: de 'denkt na'-spinner
          // duwde de hoogte net over de max en weer terug.
          scrollbarGutter: 'stable',
        }}>
          {messages.map((m, i) => (
            <Box key={i} sx={{
              alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '88%',
              px: 1.5, py: 0.75, borderRadius: 2,
              bgcolor: m.role === 'user' ? '#526075' : '#f1f5f9',
              color: m.role === 'user' ? '#fff' : 'inherit',
            }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{m.content}</Typography>
              {m.concept && (
                <Box sx={{ mt: 1, p: 1.25, borderRadius: 1.5, bgcolor: '#fff', border: '1px solid #e0e7ff' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: 13, color: '#334155' }}>{m.concept}</Typography>
                  <Button size="small" variant="contained" onClick={() => onApplyConcept(item, m.concept)}
                    sx={{ mt: 1, textTransform: 'none', fontWeight: 700 }}>
                    Gebruiken als concept
                  </Button>
                </Box>
              )}
            </Box>
          ))}
          {busy && <CircularProgress size={16} sx={{ alignSelf: 'flex-start', ml: 1 }} />}
        </Stack>
      )}
      <Stack direction="row" spacing={1} alignItems="flex-end">
        <TextField
          inputRef={inputRef}
          fullWidth multiline minRows={2} maxRows={5} size="small"
          placeholder="💬 Vraag Frits over deze mail… (C · Enter = versturen)"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
            if (e.key === 'Escape') { e.preventDefault(); e.target.blur(); }
          }}
          sx={{ bgcolor: '#fff', '& .MuiInputBase-root': { fontSize: 13.5 } }}
        />
        <Button variant="contained" onClick={() => send()} disabled={busy || !input.trim()}
          sx={{ minWidth: 44, px: 1.25, minHeight: 40 }}>
          {busy ? <CircularProgress size={16} color="inherit" /> : <SendIcon fontSize="small" />}
        </Button>
      </Stack>
      {messages.length === 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
          {CHAT_SUGGESTIONS.map((s) => (
            <Chip key={s} size="small" clickable label={s} onClick={() => send(s)}
              sx={{ fontSize: 11.5, bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }} />
          ))}
        </Stack>
      )}
    </Box>
  );
});

export default function MailAssistent() {
  const [items, setItems] = useState(null);   // null = laden
  const [idx, setIdx] = useState(0);
  const [statusFilter, setStatusFilter] = useState('open');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [notEnabled, setNotEnabled] = useState(false);
  const [actionBusy, setActionBusy] = useState(null);   // 'todo' | 'offerte' | 'planner'
  const [actionMsg, setActionMsg] = useState(null);     // { itemId, text, link? }
  const [taskDlg, setTaskDlg] = useState(null);         // taak-dialoog: naam/toelichting/voor wie/bestemming
  const [delegateDlg, setDelegateDlg] = useState(null); // delegeren: mail doorsturen naar collega
  const [colleagues, setColleagues] = useState(null);   // actieve medewerkers (lazy)
  const [moveDlg, setMoveDlg] = useState(null);         // verplaats naar map (+ vaste actie)
  const [folders, setFolders] = useState(null);         // Outlook-mappen (lazy)
  // Inline chat met Frits — direct in de kaart (geen popup). State per huidige kaart.
  const [chat, setChat] = useState({ itemId: null, messages: [], input: '', busy: false });
  const chatInputRef = useRef(null);
  const [meerAnchor, setMeerAnchor] = useState(null);   // "Meer"-menu (overflow-acties)
  const meerBtnRef = useRef(null);
  useEffect(() => { setMeerAnchor(null); }, [idx]);      // stale anchor bij kaart-wissel voorkomen
  const [showOnboarding, setShowOnboarding] = useState(() => shouldShowOnboarding()); // eerste bezoek
  const [showSignature, setShowSignature] = useState(false); // eigen handtekening bewerken
  const [showTone, setShowTone] = useState(false); // persoonlijk tone-of-voice profiel
  // Undo (← / Ctrl+Z / toast): afhandelingen terugdraaien — stapel, dus meerdere
  // stappen terug ("soms besluit ik te snel"). Toast toont de laatste actie.
  const [undoStack, setUndoStack] = useState([]); // [{ type: 'done'|'skipped'|'archive', item }]
  const [undoToast, setUndoToast] = useState(false);
  const [undoBusy, setUndoBusy] = useState(false);
  const lastAction = undoStack[undoStack.length - 1] || null;
  const pushUndo = (type, item) => { setUndoStack((s) => [...s.slice(-9), { type, item }]); setUndoToast(true); };

  // 📁 Project koppelen: dialoog met projectzoeker per mail. Onthoudt de koppeling
  // voor de hele thread + de contactpersoon (server-side).
  const [linkDlg, setLinkDlg] = useState(null); // { item, q, opts, loading }
  const linkDebounce = useRef(null);
  const searchProjects = (q) => {
    if (linkDebounce.current) clearTimeout(linkDebounce.current);
    linkDebounce.current = setTimeout(async () => {
      if (!q || q.length < 2) { setLinkDlg((d) => (d ? { ...d, opts: [] } : d)); return; }
      setLinkDlg((d) => (d ? { ...d, loading: true } : d));
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/project-search?q=${encodeURIComponent(q)}`);
        const d = await leesJson(r, 'Projecten zoeken');
        setLinkDlg((dl) => (dl ? { ...dl, opts: d.projects || [], loading: false } : dl));
      } catch (e) { setError(e.message); setLinkDlg((dl) => (dl ? { ...dl, opts: [], loading: false } : dl)); }
    }, 300);
  };
  const linkProject = async (item, proj) => {
    try {
      const payload = proj.kind === 'offerte'
        ? { offer_number: proj.offer_number, subject: proj.project_name, company_name: proj.klant_name, contact_name: proj.contactpersoon_name }
        : { airtable_project_id: proj.airtable_project_id };
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${item.id}/link-project`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Koppelen mislukt');
      // Alle items van dezelfde thread lokaal bijwerken (server deed dat ook).
      setItems((prev) => prev.map((i) =>
        (i.conversation_id && i.conversation_id === item.conversation_id) || i.id === item.id
          ? { ...i, project_match: d.project_match } : i));
      setLinkDlg(null);
    } catch (e) { setError(e.message); }
  };
  const unlinkProject = async (item) => {
    try {
      await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${item.id}/link-project`, { method: 'DELETE' });
      setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, project_match: null } : i));
    } catch (e) { setError(e.message); }
  };
  // 🔔 Offerte-reminders (apart blok, Joris 1 sep): open offertes ≥7 dagen
  // zonder opvolging. Reminder = reply-draft in de offerte-thread, draft-only.
  const [offerRem, setOfferRem] = useState({ list: [], open: true, busy: null });
  const loadOfferReminders = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/offer-reminders`);
      const d = await r.json();
      if (r.ok) setOfferRem((o) => ({ ...o, list: d.reminders || [] }));
    } catch { /* stil — blok verschijnt gewoon niet */ }
  }, []);
  useEffect(() => { loadOfferReminders(); }, [loadOfferReminders]);
  const snoozeOfferReminder = async (rem) => {
    setOfferRem((o) => ({ ...o, list: o.list.filter((x) => x.number !== rem.number) }));
    try { await authFetch(`${BACKEND_URL}/Api/mail-assistant/offer-reminders/${rem.number}/snooze`, { method: 'POST' }); } catch { /* komt vanzelf terug */ }
  };
  const maakOfferReminder = async (rem) => {
    setOfferRem((o) => ({ ...o, busy: rem.number }));
    try {
      // De verzonden offerte-mail terugvinden in de eigen mailbox (Graph),
      // zodat de reminder als reply in dezelfde thread komt.
      const zoek = [rem.number, rem.subject].filter(Boolean).join(' ');
      let results = await searchMail(`offerte ${rem.number}`, { top: 8 }).catch(() => []);
      if (!results.length && rem.subject) results = await searchMail(rem.subject, { top: 8 }).catch(() => []);
      const best = results[0];
      if (!best) throw new Error(`Geen verzonden mail gevonden voor offerte ${rem.number} (gezocht op "${zoek}") — stuur de reminder handmatig vanuit Outlook.`);
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/offer-reminders/${rem.number}/concept`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message_id: best.id }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || d.detail || 'Reminder-concept mislukt');
      setActionMsg({ itemId: null, text: `Reminder-draft voor offerte ${rem.number} staat klaar in je Outlook-concepten ✔` });
    } catch (e) { setError(e.message); }
    finally { setOfferRem((o) => ({ ...o, busy: null })); }
  };

  const [showShortcuts, setShowShortcuts] = useState(false);
  const [invCheck, setInvCheck] = useState(null);       // factuur-check: { item, loading, result }
  const [infoMsg, setInfoMsg] = useState(null);         // pagina-brede info (bv. fallback via eigen login)
  const [preview, setPreview] = useState(null);         // { open, loading, subject, body }
  const [editConcept, setEditConcept] = useState(null); // { itemId, text, saving }
  const [lessonDlg, setLessonDlg] = useState(null);     // "Leer Frits" dialog: { item, choice, text, busy }
  const [avail, setAvail] = useState({ itemId: null, date: '', start: '13:00', end: '14:00', loading: false, result: null }); // beschikbaarheidscheck
  const [availCollapsed, setAvailCollapsed] = useState({}); // { itemId: true } — kaart ingeklapt
  const [conceptOpen, setConceptOpen] = useState({});   // { itemId: true } — concept volledig tonen
  const [availPeople, setAvailPeople] = useState(null); // { itemId, lijst } — bewerkte deelnemerslijst agenda-check
  const [meetingResp, setMeetingResp] = useState({}); // { [itemId]: { sendResponse, comment, busy } } — agenda-uitnodiging
  const navigate = useNavigate();

  // Beschikbaarheidscheck over de agenda's van de (interne) deelnemers van een planning-mail.
  const runAvailability = async (item, override) => {
    const uitMail = [...new Set(
      [item.from_email, ...(item.to_recipients || []), ...(item.cc_recipients || [])]
        .filter(Boolean).map((e) => String(e).toLowerCase())
        .filter((e) => /@full-?frame\.nl$/i.test(e))
    )];
    // Bewerkte lijst (agenda-weergave: mensen toegevoegd/weggehaald) wint van de mail.
    const attendees = override?.attendees?.length ? override.attendees
      : (availPeople?.itemId === item.id && availPeople.lijst?.length) ? availPeople.lijst : uitMail;
    if (availPeople?.itemId !== item.id) setAvailPeople({ itemId: item.id, lijst: attendees });
    const date = override?.date ?? avail.date;
    const start = override?.start ?? avail.start;
    // Eindtijd altijd ná de starttijd (zelf-corrigerend i.p.v. servermelding).
    const end = endAfterStart(start, override?.end ?? avail.end);
    if (!attendees.length || !date || !start || !end) return;
    setAvail((a) => ({ ...a, itemId: item.id, date, start, end, loading: true, result: null }));
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/availability`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attendees, date, start_time: start, end_time: end, context_subject: item.subject || '' }),
      });
      const data = await r.json();
      setAvail((a) => ({ ...a, itemId: item.id, loading: false, result: r.ok ? data : { error: data.error || 'check mislukt' } }));
    } catch (e) {
      setAvail((a) => ({ ...a, loading: false, result: { error: e.message } }));
    }
  };

  // 📄 Gripp-offerte bij de mail zoeken (mirror) + 'akkoord → Geaccepteerd'-actie.
  const [offerMatch, setOfferMatch] = useState({}); // itemId -> { loading, offers, accepted, busy }
  const heeftOfferteHint = (it) => it?.category === 'offerte' || /offerte/i.test(`${it?.subject || ''} ${it?.summary || ''}`);
  const heeftAkkoordHint = (it) => /akkoord|accoord|goedgekeurd|geaccepteerd|bevestigt/i.test(`${it?.subject || ''} ${it?.summary || ''}`);
  useEffect(() => {
    const doel = (items || []).filter((it) => heeftOfferteHint(it) && offerMatch[it.id] === undefined).slice(0, 8);
    if (!doel.length) return;
    setOfferMatch((prev) => ({ ...prev, ...Object.fromEntries(doel.map((it) => [it.id, { loading: true, offers: [] }])) }));
    (async () => {
      for (const it of doel) {
        try {
          const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${it.id}/offer-match`, { method: 'POST' });
          const d = r.ok ? await r.json() : { offers: [] };
          setOfferMatch((prev) => ({ ...prev, [it.id]: { loading: false, offers: d.offers || [] } }));
        } catch {
          setOfferMatch((prev) => ({ ...prev, [it.id]: { loading: false, offers: [] } }));
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const accepteerOfferte = async (item, offer) => {
    if (!(await bevestig({ titel: `Offerte ${offer.number} op 'Geaccepteerd' zetten?`, tekst: 'De status wordt in Gripp aangepast. Daarna opent de offerte zodat je hem kunt omzetten naar een opdracht.', knop: 'Accepteren' }))) return;
    setOfferMatch((prev) => ({ ...prev, [item.id]: { ...prev[item.id], busy: true } }));
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${item.id}/offer-accept`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gripp_id: offer.gripp_id }),
      });
      if (!r.ok) throw new Error((await r.json()).error || 'Accepteren mislukt');
      setOfferMatch((prev) => ({ ...prev, [item.id]: {
        ...prev[item.id], busy: false,
        offers: (prev[item.id]?.offers || []).map((o) => o.gripp_id === offer.gripp_id ? { ...o, phase: 3, phase_name: 'Geaccepteerd' } : o),
      } }));
      setActionMsg({ itemId: item.id, text: `✅ Offerte ${offer.number} staat op Geaccepteerd — rond het omzetten naar een opdracht af in Gripp.` });
      const url = buildGrippOfferUrl(offer.gripp_id);
      if (url) window.open(url, '_blank', 'noopener');
    } catch (e) {
      setOfferMatch((prev) => ({ ...prev, [item.id]: { ...prev[item.id], busy: false } }));
      setActionMsg({ itemId: item.id, text: `⚠ ${e.message}` });
    }
  };

  // Offerte-PDF als bijlage aan het concept hangen.
  const [attachOffer, setAttachOffer] = useState({ itemId: null, number: '', busy: false });
  const doAttachOffer = async (item) => {
    const num = (attachOffer.number || item.project_match?.project_number || '').toString().trim();
    if (!num) { setActionMsg({ itemId: item.id, text: 'Vul eerst een offertenummer in.' }); return; }
    setAttachOffer((a) => ({ ...a, itemId: item.id, busy: true }));
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${item.id}/attach-offer`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ offerNumber: num }),
      });
      const d = await r.json();
      if (r.ok) {
        setItems((prev) => prev.map((it) => it.id === item.id ? { ...it, draft_created: true } : it));
        setActionMsg({ itemId: item.id, text: `Offerte ${num} als bijlage aan het concept gehangen ✔` });
      } else {
        setActionMsg({ itemId: item.id, text: d.error || 'Bijlage toevoegen mislukt.' });
      }
    } catch (e) {
      setActionMsg({ itemId: item.id, text: e.message });
    } finally { setAttachOffer((a) => ({ ...a, busy: false })); }
  };

  // Scope-check: vergelijk de mail met de offerte, waarschuw bij scope-wijziging.
  const [scope, setScope] = useState({ itemId: null, busy: false, result: null });
  const doScopeCheck = async (item) => {
    const num = (attachOffer.itemId === item.id && attachOffer.number) || item.project_match?.project_number || '';
    setScope({ itemId: item.id, busy: true, result: null });
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${item.id}/scope-check`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectNumber: num }),
      });
      const d = await r.json();
      setScope({ itemId: item.id, busy: false, result: r.ok ? d : { error: d.error || 'mislukt' } });
    } catch (e) {
      setScope({ itemId: item.id, busy: false, result: { error: e.message } });
    }
  };

  // ✏️ Offerte aanpassen — open de bestaande offerte-generator ("aanpassen"-modus)
  // voorgevuld met dit offertenummer + de mail als wijzigingsverzoek. Daar zie je de
  // delta's (nieuw/aangepast/verwijderd) en pas ná akkoord wordt het naar Gripp
  // teruggeschreven (met de bestaande fase-guardrail: alleen concept/verzonden).
  const openOfferteAanpassen = async (item) => {
    const num = ((attachOffer.itemId === item.id && attachOffer.number) || item.project_match?.project_number || '').toString().trim();
    if (!num) { setError('Vul eerst het offertenummer in om de offerte aan te passen.'); return; }
    // Rijke context meesturen — een korte samenvatting is te weinig voor de offerte-
    // generator. Stuur de VOLLEDIGE THREAD mee (incl. latere reacties, bv. de collega
    // die apart de lengte per animatie uitzocht) + het gesprek met Frits.
    let thread = '';
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${item.id}/thread`);
      if (r.ok) { const d = await r.json(); thread = d.thread || ''; }
    } catch { /* thread is optioneel */ }
    if (!thread) {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${item.id}/body`);
        if (r.ok) { const d = await r.json(); thread = d.body || ''; }
      } catch { /* val terug op de samenvatting */ }
    }
    const gesprek = (chatFor(item).messages || [])
      .map((m) => `${m.role === 'user' ? 'Ik' : 'Frits'}: ${m.content}`).join('\n');
    const change = [
      `Wijzigingsverzoek voor de offerte, op basis van deze mailthread: "${item.subject || ''}".`,
      thread ? `--- Volledige mailthread (chronologisch, incl. reacties van collega's) ---\n${thread.slice(0, 9000)}` : (item.summary || ''),
      gesprek ? `--- Gesprek met Frits (instructies + context) ---\n${gesprek}` : '',
    ].filter(Boolean).join('\n\n');
    try { sessionStorage.setItem('frits-offerte-change', change); } catch { /* prefill is optioneel */ }
    navigate(`/offertes?mode=aanpassen&number=${encodeURIComponent(num)}`);
  };

  // "Wie kan dit oppakken?" — Slimme toewijzing inline in de mailkaart.
  const [assign, setAssign] = useState({ itemId: null, role: 'Editor', loading: false, candidates: null });
  const runAssign = async (item) => {
    setAssign((a) => ({ ...a, itemId: item.id, loading: true, candidates: null }));
    try {
      const end = new Date(); end.setDate(end.getDate() + 14);
      const qs = `roles[]=${encodeURIComponent(assign.role)}&startDate=${formatDateISO(new Date())}&endDate=${end.toISOString().slice(0, 10)}`;
      const r = await authFetch(`${BACKEND_URL}/Api/capacity-by-role?${qs}`);
      const d = await leesJson(r, 'Beschikbare collega\'s zoeken');
      const list = (d[assign.role] || []).filter((c) => c._matched).slice(0, 5);
      setAssign((a) => ({ ...a, itemId: item.id, loading: false, candidates: list }));
    } catch (e) {
      setError(e.message); // niet stil: "niemand beschikbaar" zou een verkeerde conclusie zijn
      setAssign((a) => ({ ...a, loading: false, candidates: [] }));
    }
  };

  // Zoek per kwartier een vrij tijdslot van de juiste duur (binnen het gekozen venster).
  const [findingSlot, setFindingSlot] = useState(false);
  const findSlot = async (item) => {
    const attendees = [...new Set(
      [item.from_email, ...(item.to_recipients || []), ...(item.cc_recipients || [])]
        .filter(Boolean).map((e) => String(e).toLowerCase()).filter((e) => /@full-?frame\.nl$/i.test(e))
    )];
    if (!attendees.length || !avail.date) return;
    const toMin = (t) => { const [h, m] = String(t).split(':').map(Number); return h * 60 + (m || 0); };
    const duration = Math.max(15, toMin(avail.end) - toMin(avail.start)) || 60;
    setFindingSlot(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/find-slot`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attendees, date: avail.date, duration_minutes: duration, window_start: avail.start, window_end: avail.end }),
      });
      const d = await leesJson(r, 'Tijdslot zoeken'); // fout ≠ "geen slot gevonden"
      if (d.found) {
        setAvail((a) => ({ ...a, start: d.start_time, end: d.end_time, result: null }));
        setActionMsg({ itemId: item.id, text: `Gevonden: ${d.start_time}–${d.end_time} past voor iedereen. Klik "Check" of "Zet in het antwoord".` });
      } else {
        setActionMsg({ itemId: item.id, text: 'Geen vrij tijdslot gevonden in dit venster — probeer een ander dagdeel of dag.' });
      }
    } catch (e) {
      setActionMsg({ itemId: item.id, text: e.message });
    } finally { setFindingSlot(false); }
  };

  // Concept-mail naar een collega om een conflicterende interne afspraak te verzetten (draft-only).
  const [reschedule, setReschedule] = useState({ key: null, loading: false, link: null, error: null });
  const runReschedule = async (item, att, conflict) => {
    const key = `${item.id}:${att.email}`;
    setReschedule({ key, loading: true, link: null, error: null });
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/reschedule-draft`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_email: att.email, to_name: att.email.split('@')[0],
          meeting_subject: item.subject || '', date: avail.date, time: `${avail.start}-${avail.end}`,
          conflict_subject: conflict.subject || '',
        }),
      });
      const data = await r.json();
      setReschedule({ key, loading: false, link: r.ok ? data.web_link : null, error: r.ok ? null : (data.error || 'mislukt') });
    } catch (e) {
      setReschedule({ key, loading: false, link: null, error: e.message });
    }
  };

  // Gekozen tijdslot in het concept-antwoord zetten (herschrijft concept + Outlook-draft).
  const [applyingSlot, setApplyingSlot] = useState(false);
  const applySlot = async (item) => {
    setApplyingSlot(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${item.id}/apply-slot`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: avail.date, start_time: avail.start, end_time: avail.end }),
      });
      const data = await r.json();
      if (r.ok && data.concept) {
        setItems((prev) => prev.map((it) => it.id === item.id ? { ...it, concept: data.concept, draft_created: true } : it));
        setActionMsg({ itemId: item.id, text: `Tijdstip (${avail.start}–${avail.end}) in het concept-antwoord gezet — draft bijgewerkt in Outlook ✔` });
      } else {
        setActionMsg({ itemId: item.id, text: data.error || 'Kon het tijdstip niet toepassen.' });
      }
    } catch (e) {
      setActionMsg({ itemId: item.id, text: e.message });
    } finally { setApplyingSlot(false); }
  };

  // Slot-extractie: haal voorgestelde afspraak-momenten uit een planning-mail (gecached per item).
  const [slotsByItem, setSlotsByItem] = useState({});
  const fetchSlots = useCallback(async (item) => {
    setSlotsByItem((m) => (m[item.id] ? m : { ...m, [item.id]: { loading: true, list: [], notes: '' } }));
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${item.id}/extract-slots`, { method: 'POST' });
      const data = await leesJson(r, 'Afspraakmomenten uit de mail halen');
      const list = Array.isArray(data.slots) ? data.slots : [];
      setSlotsByItem((m) => ({ ...m, [item.id]: { loading: false, list, notes: data.notes || '' } }));
      if (list.length) {
        const s = list[0];
        // Automatisch: zoek een passend kwartier-slot binnen het venster en analyseer meteen.
        const attendees = [...new Set(
          [item.from_email, ...(item.to_recipients || []), ...(item.cc_recipients || [])]
            .filter(Boolean).map((e) => String(e).toLowerCase()).filter((e) => /@full-?frame\.nl$/i.test(e))
        )];
        const toMin = (t) => { const [h, m] = String(t || '').split(':').map(Number); return (h || 0) * 60 + (m || 0); };
        const dur = s.duration_minutes || Math.max(15, toMin(s.end_time) - toMin(s.start_time)) || 60;
        let start = s.start_time, end = s.end_time;
        try {
          if (attendees.length && s.date) {
            const fr = await authFetch(`${BACKEND_URL}/Api/mail-assistant/find-slot`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ attendees, date: s.date, duration_minutes: dur, window_start: s.start_time, window_end: s.end_time }),
            });
            const fd = await fr.json();
            if (fr.ok && fd.found) { start = fd.start_time; end = fd.end_time; }
          }
        } catch { /* val terug op het venster */ }
        setAvail((a) => ({ ...a, itemId: item.id, date: s.date || a.date, start: start || a.start, end: end || a.end, result: null }));
        runAvailability(item, { date: s.date, start, end });
      }
    } catch (e) {
      setError(e.message); // één keer per kaart (gecached in slotsByItem), dus geen herhaal-meldingen
      setSlotsByItem((m) => ({ ...m, [item.id]: { loading: false, list: [], notes: '' } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const it = items?.[idx];
    if (it && it.category === 'planning' && !slotsByItem[it.id]) fetchSlots(it);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, items]);

  // Visuele agenda-weergave (pop-up)
  const [visualOpen, setVisualOpen] = useState(false);
  const DAY_START = 8 * 60, DAY_END = 19 * 60;   // tijdlijn 08:00–19:00
  const toMin = (hhmm) => {
    const [h, m] = String(hhmm || '').split(':').map(Number);
    return Number.isNaN(h) ? null : h * 60 + (m || 0);
  };
  const pct = (mins) => Math.max(0, Math.min(100, ((mins - DAY_START) / (DAY_END - DAY_START)) * 100));
  const CONFLICT_COLORS = { internal: '#f59e0b', external: '#ef4444', draaidag: '#7c3aed' };
  // Overlappende afspraken over sub-banen verdelen (greedy) zodat niets overlapt.
  const laneLayout = (events) => {
    const evs = (events || []).map((e) => ({
      ...e,
      s: e.all_day ? DAY_START : (toMin(e.start) ?? DAY_START),
      e2: e.all_day ? DAY_END : (toMin(e.end) ?? DAY_START),
    })).sort((a, b) => a.s - b.s);
    const laneEnds = [];
    for (const e of evs) {
      let placed = false;
      for (let i = 0; i < laneEnds.length; i++) {
        if (e.s >= laneEnds[i]) { e.lane = i; laneEnds[i] = e.e2; placed = true; break; }
      }
      if (!placed) { e.lane = laneEnds.length; laneEnds.push(e.e2); }
    }
    return { events: evs, lanes: Math.max(1, laneEnds.length) };
  };
  const { instance: msalInstance, accounts } = useMsal();
  const isMobile = useMediaQuery('(max-width:600px)');
  const touchStart = useRef(null);   // swipe: rechts = klaar, links = overslaan
  const myEmail = (accounts[0]?.username || '').toLowerCase();
  const isMe = useCallback((addr) => {
    const a = String(addr || '').toLowerCase();
    return a === myEmail || a === myEmail.replace('@fullframe.nl', '@full-frame.nl') || a === myEmail.replace('@full-frame.nl', '@fullframe.nl');
  }, [myEmail]);
  // Compacte adressenlijst: eigen adres wordt "jij", verder alleen het deel vóór de @.
  const addrList = useCallback((arr, max = 3) => {
    const names = (arr || []).map((a) => (isMe(a) ? 'jij' : String(a).split('@')[0]));
    return names.slice(0, max).join(', ') + (names.length > max ? ` +${names.length - max}` : '');
  }, [isMe]);

  useEffect(() => {
    if (msalInstance && accounts.length > 0) initMicrosoftTodoService(msalInstance, accounts[0]);
  }, [msalInstance, accounts]);

  // 📲 App-modus: op deze pagina het Frits-manifest gebruiken, zodat "Zet op
  // beginscherm" een eigen Frits-app oplevert (icoon + opent direct hier).
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const prev = link?.getAttribute('href');
    if (link) link.setAttribute('href', '/frits-manifest.json');
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
    return () => { if (link && prev) link.setAttribute('href', prev); };
  }, []);

  // 🔔 Ochtend-push: status + aanzetten (werkt op iPhone alleen als de app op
  // het beginscherm staat — iOS-eis voor web-push).
  const [pushState, setPushState] = useState('unknown'); // unknown|unsupported|off|on|busy
  useEffect(() => {
    (async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) { setPushState('unsupported'); return; }
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setPushState(sub ? 'on' : 'off');
      } catch { setPushState('off'); }
    })();
  }, []);
  const enablePush = async () => {
    setPushState('busy');
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setPushState('off'); setError('Meldingen zijn geweigerd — zet ze aan in je browser/telefoon-instellingen.'); return; }
      const kr = await authFetch(`${BACKEND_URL}/Api/mail-assistant/push/vapid-key`);
      const { key } = await leesJson(kr, 'Push-sleutel ophalen');
      if (!key) throw new Error('Push is nog niet geconfigureerd op de server');
      const raw = atob(key.replace(/-/g, '+').replace(/_/g, '/').padEnd(key.length + (4 - (key.length % 4)) % 4, '='));
      const appKey = new Uint8Array([...raw].map((c) => c.charCodeAt(0)));
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appKey });
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/push/subscribe`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription: sub.toJSON() }),
      });
      if (!r.ok) throw new Error('Registreren mislukt');
      setPushState('on');
      setInfoMsg('Ochtend-melding staat aan: je krijgt een push zodra Frits om 07:30 je triage klaar heeft. 📬');
    } catch (e) { setPushState('off'); setError(`Meldingen aanzetten mislukt: ${e.message}`); }
  };

  const load = useCallback(async (filter = statusFilter) => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items?status=${filter}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Laden mislukt');
      setItems(d.items || []);
      setIdx(0);
    } catch (e) { setError(e.message); setItems([]); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Fallback zolang de Exchange access policy de server-token blokkeert:
  // mail ophalen met je EIGEN login (zelfde route als de offerte-generator),
  // triage server-side, en de concept-drafts ook via je eigen login in Outlook.
  const fallbackRun = async () => {
    const since = new Date(Date.now() - 26 * 3600 * 1000).toISOString().replace(/\.\d+Z$/, 'Z');
    const mails = await fetchRecentInbox(since);
    let sentSamples = null;
    if (!mails.length) return { processed: 0 };
    try { sentSamples = await fetchRecentSent(); } catch { /* profiel bestaat mogelijk al */ }
    const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/run-batch`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mails, sent_samples: sentSamples }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || 'Run (eigen login) mislukt');
    // Concepten blijven in de tooling — naar Outlook pas via de knop
    // "Zet in Outlook" op de kaart (bewuste stap, geen automatische drafts).
    return d;
  };

  const runNow = async () => {
    setRunning(true); setError(null); setNotEnabled(false);
    const act = startActivity('Frits: mails checken', { path: '/mail-assistent' });
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/run`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) {
        // Graph 403 = access policy blokkeert de server-token (nog) → probeer via eigen login.
        if (r.status === 403 || /403|Access.*denied|AccessPolicy/i.test(d.error || '')) {
          try {
            await fallbackRun();
            setInfoMsg('Frits werkte via jouw eigen login — de server-toegang wordt nog door Microsoft doorgevoerd, daarna gaat dit automatisch.');
          } catch (fbErr) {
            setNotEnabled(true);
            console.warn('[Frits] fallback via eigen login mislukt:', fbErr.message);
            return;
          }
        } else {
          throw new Error(d.error || 'Run mislukt');
        }
      }
      await load('open');
      setStatusFilter('open');
      act.done('Check afgerond — items staan klaar');
    } catch (e) { setError(e.message); act.fail(e.message); } finally { setRunning(false); }
  };

  // Afhandelen is OPTIMISTIC: de kaart wisselt direct (server werkt op de
  // achtergrond; bij een fout komt de kaart terug). De 400ms-lock voorkomt dat
  // een dubbele klik/toets per ongeluk óók de volgende mail afhandelt.
  const actionLockRef = useRef(0);
  const takeActionSlot = () => {
    const now = Date.now();
    if (now - actionLockRef.current < 400) return false;
    actionLockRef.current = now;
    return true;
  };
  const removeCard = (id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    setIdx((prev) => Math.min(prev, Math.max(0, (items?.length || 1) - 2)));
  };
  const restoreCard = (it) => setItems((prev) => (prev.some((i) => i.id === it.id) ? prev : [it, ...prev]));

  const handle = async (item, status) => {
    if (!takeActionSlot()) return;
    removeCard(item.id);
    pushUndo(status, item);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${item.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, feedback: { handled_via: 'triage-ui' } }),
      });
      if (r.status === 404) return;   // item bestond al niet meer (bv. opgeruimd duplicaat) — weg is weg
      if (!r.ok) throw new Error('Afhandelen mislukt');
    } catch (e) {
      setError(`${e.message} — kaart teruggezet`); restoreCard(item);
      setUndoStack((s) => s.filter((x) => x.item.id !== item.id));
    }
  };

  // ↩️ Undo: laatste afhandeling terugdraaien (← of Ctrl/Cmd+Z of toast). Stapel:
  // meerdere keren terug kan.
  const undoLast = async () => {
    const la = undoStack[undoStack.length - 1];
    if (!la || undoBusy) return;
    setUndoBusy(true); setError(null);
    try {
      if (la.type === 'archive') {
        const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${la.item.id}/unarchive`, { method: 'POST' });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || 'Ongedaan maken mislukt');
      } else {
        const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${la.item.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'open', feedback: { undo: true } }),
        });
        if (!r.ok) throw new Error('Ongedaan maken mislukt');
      }
      restoreCard(la.item);
      setUndoStack((s) => s.slice(0, -1));
      setUndoToast(false);
    } catch (e) { setError(e.message); }
    finally { setUndoBusy(false); }
  };

  // 📥 Concept uit de tooling alsnog als reply-draft in Outlook zetten — de
  // bewuste éénknops-stap (triage maakt zelf geen drafts meer aan).
  const [pushingDraft, setPushingDraft] = useState(null);
  const pushToOutlook = async (it) => {
    if (pushingDraft) return;
    setPushingDraft(it.id);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${it.id}/create-draft`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Outlook-draft aanmaken mislukt');
      setItems((prev) => prev.map((i) => i.id === it.id
        ? { ...i, draft_created: true, draft_message_id: d.item?.draft_message_id || i.draft_message_id }
        : i));
      setActionMsg({ itemId: it.id, text: 'Concept staat nu als draft in je Outlook-thread ✔' });
    } catch (e) {
      setActionMsg({ itemId: it.id, text: `⚠️ ${e.message}` });
    } finally { setPushingDraft(null); }
  };

  // 🗑 Concept verwijderen: Outlook-draft weg + concept van de kaart.
  // Telt als leersignaal — "hier was geen antwoord nodig".
  const deleteConcept = async (it) => {
    if (!(await bevestig({ titel: 'Concept verwijderen?', tekst: 'Ook uit je Outlook-concepten.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${it.id}/draft`, { method: 'DELETE' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Concept verwijderen mislukt');
      setItems((prev) => prev.map((i) => i.id === it.id ? { ...i, concept: null, draft_created: false, draft_message_id: null } : i));
      setActionMsg({ itemId: it.id, text: 'Concept verwijderd — ook uit je Outlook-concepten ✔' });
    } catch (e) { setError(e.message); }
  };

  // 📤 Concept versturen vanuit Frits — testfase: server weigert externe ontvangers.
  const isInternalAddr = (a) => {
    const low = String(a || '').toLowerCase();
    return low.endsWith('@fullframe.nl') || low.endsWith('@full-frame.nl');
  };
  // Uitgesteld (5 s, Gmail-stijl): de kaart blijft staan en dimt tot de mail écht weg is;
  // "Annuleren" in de melding = er is niets verstuurd.
  const zetVerzendend = (id, aan) => setItems((prev) => (prev || []).map((i) => (i.id === id ? { ...i, verzendend: aan } : i)));
  const sendConcept = async (it) => {
    if (actionBusy || !takeActionSlot()) return;
    setActionBusy('send'); setError(null);
    zetVerzendend(it.id, true);
    const r = await voerUitgesteldUit(`Antwoord aan ${it.from_name || it.from_email} wordt verzonden…`, async () => {
      const res = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${it.id}/send`, { method: 'POST' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Versturen mislukt');
      return d;
    }, { duur: 5000 });
    setActionBusy(null);
    if (r.geannuleerd || r.fout) {
      zetVerzendend(it.id, false);
      if (r.fout) setError(r.fout.message);
      return;
    }
    const d = r.resultaat || {};
    removeCard(it.id);
    setActionMsg({ itemId: null, text: `Verstuurd naar ${(d.recipients || []).join(', ')}${d.archived ? ' + origineel gearchiveerd' : ''} ✔` });
  };

  // 💬 Inline chat met Frits — thread-bewuste chat per mail, direct in de kaart
  // (geen popup). Vragen stellen, concept aanpassen/maken.
  const chatFor = (it) => (chat.itemId === it.id ? chat : { itemId: it.id, messages: [], input: '', busy: false });
  const sendChat = async (item, text) => {
    const cur = chatFor(item);
    const content = (text || cur.input || '').trim();
    if (!content || cur.busy) return;
    const messages = [...cur.messages, { role: 'user', content }];
    setChat({ itemId: item.id, messages, input: '', busy: true });
    const act = startActivity('Frits denkt na', { path: '/mail-assistent' });
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${item.id}/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Frits kon niet antwoorden');
      setChat((c) => (c.itemId === item.id ? {
        ...c, busy: false,
        messages: [...messages, { role: 'assistant', content: d.reply, concept: d.concept || null }],
      } : c));
      act.done('Antwoord staat klaar');
    } catch (e) {
      setChat((c) => (c.itemId === item.id
        ? { ...c, busy: false, messages: [...messages, { role: 'assistant', content: `⚠️ ${e.message}` }] } : c));
      act.fail(e.message);
    }
  };
  const applyChatConcept = (it, conceptText) => {
    setEditConcept({ itemId: it.id, text: conceptText });
  };

  // ✅ "Opgepakt?": is de collega in 'Aan' al in actie gekomen ná deze mail?
  // Zo niet: reminder-concept aan die collega (draft-only).
  const followUpTarget = (it) => (it?.to_recipients || []).find(
    (a) => (a.endsWith('@fullframe.nl') || a.endsWith('@full-frame.nl')) && !isMe(a)
  );
  const followUp = async (it) => {
    if (actionBusy) return;
    setActionBusy('followup'); setError(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${it.id}/follow-up`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Check mislukt');
      if (d.responded) {
        setActionMsg({ itemId: it.id, text: `✅ ${d.owner_first} reageerde ${d.when}: “${d.preview}”` });
      } else {
        setActionMsg({ itemId: it.id, text: `⏳ Nog geen reactie van ${d.owner_first} — reminder (“is dit al gelukt?”) staat als concept klaar`, link: d.draft_web_link });
      }
    } catch (e) { setError(e.message); } finally { setActionBusy(null); }
  };

  // 🧾 Factuur-check: PDF-bijlage → bedrag + opdrachtnummer → Gripp-regels.
  const looksLikeInvoice = (it) => /factuur|invoice|bon(netje)?|receipt|ontvangstbewijs|creditnota/i
    .test(`${it?.subject || ''} ${it?.summary || ''}`);
  const runInvoiceCheck = async (it) => {
    setInvCheck({ item: it, loading: true, result: null });
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${it.id}/invoice-check`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Factuur-check mislukt');
      setInvCheck({ item: it, loading: false, result: d });
    } catch (e) {
      setInvCheck({ item: it, loading: false, result: { verdict: 'fout', toelichting: e.message } });
    }
  };
  // Uitgesteld (5 s): dialoog blijft open met de knop uitgeschakeld; annuleren = niets gebeurd.
  const forwardToFactuur = async (it) => {
    setInvCheck((d) => ({ ...d, forwarding: true }));
    const r = await voerUitgesteldUit('Factuur wordt doorgestuurd naar factuur@fullframe.nl en gearchiveerd…', async () => {
      const res = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${it.id}/auto-forward`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: 'factuur@fullframe.nl', then_archive: true, remember: false }),
      });
      if (!res.ok) throw new Error('Doorsturen mislukt');
    }, { duur: 5000 });
    if (r.geannuleerd || r.fout) {
      if (r.fout) setError(r.fout.message);
      setInvCheck((d) => (d ? { ...d, forwarding: false } : d));
      return;
    }
    setInvCheck(null);
    setItems((prev) => prev.filter((i) => i.id !== it.id));
    setActionMsg({ itemId: -1, text: '' });
    setInfoMsg('Doorgestuurd naar factuur@fullframe.nl en gearchiveerd ✔');
  };

  // 📁 Verplaatsen naar map — met optioneel "voortaan automatisch" (vaste actie).
  const openMoveDlg = async (it) => {
    setMoveDlg({ item: it, mode: 'map', folder: null, forwardTo: '', thenArchive: true, remember: false, busy: false });
    if (!folders) {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/folders`);
        const d = await leesJson(r, 'Mappen laden');
        setFolders(d.folders || []);
      } catch (e) { setError(e.message); setFolders([]); }
    }
  };
  const submitMove = async () => {
    const { item: it, mode, folder, forwardTo, thenArchive, remember } = moveDlg;
    const isForward = mode === 'forward';
    if (isForward ? !(forwardTo || '').includes('@') : !folder) return;
    // Alleen de variant die een blijvende regel aanmaakt vraagt om bevestiging.
    if (remember) {
      const tekst = isForward
        ? `Mails van ${it.from_email} worden voortaan automatisch doorgestuurd naar ${forwardTo.trim()}${thenArchive ? ' en gearchiveerd' : ''}.`
        : `Mails van ${it.from_email} gaan voortaan automatisch naar "${folder.name}".`;
      if (!(await bevestig({ titel: 'Voortaan automatisch zo doen?', tekst, knop: 'Ja, voortaan automatisch' }))) return;
    }
    setMoveDlg((d) => ({ ...d, busy: true }));
    try {
      const url = isForward
        ? `${BACKEND_URL}/Api/mail-assistant/items/${it.id}/auto-forward`
        : `${BACKEND_URL}/Api/mail-assistant/items/${it.id}/move`;
      const payload = isForward
        ? { to: forwardTo.trim(), then_archive: thenArchive, remember }
        : { folder_id: folder.id, folder_name: folder.name, remember };
      const r = await authFetch(url, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || (isForward ? 'Doorsturen mislukt' : 'Verplaatsen mislukt'));
      setMoveDlg(null);
      setItems((prev) => prev.filter((i) => i.id !== it.id));
      setIdx((prev) => Math.min(prev, Math.max(0, (items?.length || 1) - 2)));
      if (d.remembered) {
        setInfoMsg(isForward
          ? `Vaste actie ingesteld: mails van ${it.from_email} worden voortaan automatisch doorgestuurd naar ${forwardTo.trim()}${thenArchive ? ' en gearchiveerd' : ''}.`
          : `Vaste actie ingesteld: mails van ${it.from_email} gaan voortaan automatisch naar "${folder.name}".`);
      }
    } catch (e) { setError(e.message); setMoveDlg((d) => (d ? { ...d, busy: false } : d)); }
  };

  // 🗑 Verwijderen: mail naar de Outlook-prullenbak + item afronden.
  // Zelfde undo-pad als archiveren (unarchive zet hem terug in de inbox).
  const verwijderMail = async (item) => {
    if (!takeActionSlot()) return;
    removeCard(item.id);
    pushUndo('archive', item);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${item.id}/delete-mail`, { method: 'POST' });
      if (r.status === 404) return;
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Verwijderen mislukt');
    } catch (e) {
      setError(`${e.message} — kaart teruggezet`); restoreCard(item);
      setUndoStack((s2) => s2.filter((x) => x.item.id !== item.id));
    }
  };

  // ✉️ Extern antwoorden: concept aan het oorspronkelijke contact van een
  // geforwarde thread (niet de interne collega). Draft-only.
  const [externDlg, setExternDlg] = useState(null); // { item, loading, candidates, to, instructie, busy, result }
  const openExternDlg = async (item) => {
    setExternDlg({ item, loading: true, candidates: [], to: '', instructie: '', busy: false, result: null });
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${item.id}/extern-concept`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      const d = r.ok ? await r.json() : { candidates: [] };
      setExternDlg((dlg) => (dlg ? { ...dlg, loading: false, candidates: d.candidates || [], to: (d.candidates || [])[0] || '' } : dlg));
    } catch {
      setExternDlg((dlg) => (dlg ? { ...dlg, loading: false } : dlg));
    }
  };
  const maakExternConcept = async () => {
    const dlg = externDlg;
    if (!dlg?.to.trim() || dlg.busy) return;
    setExternDlg((d) => ({ ...d, busy: true }));
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${dlg.item.id}/extern-concept`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_email: dlg.to.trim(), instruction: dlg.instructie || '' }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Concept maken mislukt');
      setExternDlg((prev) => (prev ? { ...prev, busy: false, result: d } : prev));
    } catch (e) {
      setError(e.message);
      setExternDlg((prev) => (prev ? { ...prev, busy: false } : prev));
    }
  };

  // 🗄 Archiveren: mail naar de Outlook-archiefmap + item afronden.
  const archive = async (item) => {
    if (!takeActionSlot()) return;
    removeCard(item.id);
    pushUndo('archive', item);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${item.id}/archive`, { method: 'POST' });
      if (r.status === 404) return;   // item bestond al niet meer — weg is weg
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Archiveren mislukt');
    } catch (e) {
      setError(`${e.message} — kaart teruggezet`); restoreCard(item);
      setUndoStack((s) => s.filter((x) => x.item.id !== item.id));
    }
  };

  // Actie-feedback loggen op het item (self-learning: welke voorstellen worden gebruikt).
  const logAction = (item, key) => authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${item.id}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ feedback: { [key]: true } }),
  }).catch(() => {});

  // Collega's laden (voor taak-toewijzing en delegeren) + beschikbaarheids-check.
  const loadColleagues = useCallback(async () => {
    if (colleagues) return colleagues;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/colleagues`);
      const d = await leesJson(r, 'Collega\'s laden');
      setColleagues(d.colleagues || []);
      return d.colleagues || [];
    } catch (e) { setError(e.message); setColleagues([]); return []; }
  }, [colleagues]);

  const checkToday = async (name, setDlg) => {
    setDlg((dlg) => (dlg ? { ...dlg, today: { loading: true } } : dlg));
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/colleague-today?name=${encodeURIComponent(name)}`);
      const d = await leesJson(r, 'Agenda van collega checken');
      setDlg((dlg) => (dlg ? { ...dlg, today: { loading: false, busy: !!d.busy, items: d.items || [] } } : dlg));
    } catch (e) {
      setError(e.message);
      setDlg((dlg) => (dlg ? { ...dlg, today: null } : dlg));
    }
  };

  // ✅ Taak-dialoog: bewerkbare titel + toelichting, voor jezelf of een collega,
  // naar Airtable (team) of Microsoft To-Do.
  const openTaskDlg = async (item, action, destination = 'todo') => {
    setTaskDlg({
      item,
      name: action?.label || item.subject || 'Taak uit mail',
      notes: [item.summary, `Uit mail: "${item.subject}" van ${item.from_name || item.from_email}`].filter(Boolean).join('\n\n'),
      assignee: null,   // null = mijzelf
      destination,
      busy: false, today: null,
    });
    loadColleagues();
  };
  const submitTask = async () => {
    const { item, name, notes, assignee, destination } = taskDlg;
    setTaskDlg((d) => ({ ...d, busy: true }));
    try {
      if (destination === 'todo') {
        if (assignee && !isMe(assignee.email)) {
          const r = await authFetch(`${BACKEND_URL}/Api/todo/create-for-user`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: assignee.email, tasks: [{ title: name, body: notes }] }),
          });
          if (!r.ok) throw new Error('To-Do voor collega mislukt');
        } else {
          const result = await createMicrosoftTodoTasks([{ name, body: notes }]);
          if (!result.created?.length) throw new Error('To-Do aanmaken mislukt');
        }
      } else {
        const r = await authFetch(`${BACKEND_URL}/Api/tasks/create-batch`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tasks: [{ name, source: notes, ...(assignee ? { assigneeId: assignee.id } : {}) }] }),
        });
        const d = await r.json();
        if (!r.ok || !d.success) throw new Error(d.error || 'Taak aanmaken mislukt');
      }
      logAction(item, 'action_todo');
      setTaskDlg(null);
      setActionMsg({ itemId: item.id, text: `Taak aangemaakt${assignee ? ` voor ${assignee.name}` : ''} ✔` });
    } catch (e) { setError(e.message); setTaskDlg((d) => (d ? { ...d, busy: false } : d)); }
  };

  // 📨 Delegeren: mail doorsturen naar een collega (direct of als concept) —
  // met beschikbaarheids-check vóór je iets belooft.
  const openDelegateDlg = async (item) => {
    const myName = (accounts[0]?.name || myEmail.split('@')[0] || '').split(' ')[0];
    setDelegateDlg({
      item, colleague: null,
      comment: `Hoi ,\n\nKun jij deze oppakken?\n${item.summary ? `\nKort: ${item.summary}\n` : ''}\nGroet, ${myName}`,
      makeTask: false, busy: false, today: null,
    });
    loadColleagues();
  };
  const submitDelegate = async (send) => {
    const { item, colleague, comment, makeTask } = delegateDlg;
    if (!colleague) return;
    setDelegateDlg((d) => ({ ...d, busy: true }));
    try {
      const doorsturen = () => forwardMail(item.message_id, colleague.email, comment, { send });
      let result;
      if (send) {
        // Direct versturen: uitgesteld (5 s) — dialoog blijft open, annuleren = niets gebeurd.
        const r = await voerUitgesteldUit(`Mail naar ${colleague.name} wordt doorgestuurd…`, doorsturen, { duur: 5000 });
        if (r.geannuleerd) { setDelegateDlg((d) => (d ? { ...d, busy: false } : d)); return; }
        if (r.fout) throw r.fout;
        result = r.resultaat || {};
      } else {
        result = await doorsturen();
      }
      if (makeTask) {
        try {
          await authFetch(`${BACKEND_URL}/Api/tasks/create-batch`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tasks: [{ name: item.subject || 'Oppakken (mail)', source: `Gedelegeerd via Frits — mail van ${item.from_name || item.from_email}\n${item.summary || ''}`, assigneeId: colleague.id }] }),
          });
        } catch (taskErr) { console.warn('[Frits] taak bij delegeren mislukt:', taskErr.message); }
      }
      logAction(item, 'action_delegate');
      setDelegateDlg(null);
      setActionMsg({
        itemId: item.id,
        text: send ? `Doorgestuurd naar ${colleague.name}${makeTask ? ' + taak aangemaakt' : ''} ✔`
                   : `Concept-forward aan ${colleague.name} staat in je Outlook-concepten${makeTask ? ' + taak aangemaakt' : ''} ✔`,
        link: result.webLink || null,
      });
    } catch (e) { setError(e.message); setDelegateDlg((d) => (d ? { ...d, busy: false } : d)); }
  };

  // 🧾 Offerte Generator openen met deze mail als bron.
  const doOfferte = async (item) => {
    setActionBusy('offerte'); setError(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${item.id}/body`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Mail ophalen mislukt');
      logAction(item, 'action_offerte');
      navigate('/offertes', {
        state: {
          sourceText: `E-mail van ${d.from_name || ''} <${d.from_email || ''}> — ${d.subject || ''}\n\n${d.body || ''}`,
          sourceLabel: d.subject || 'Mail',
        },
      });
    } catch (e) { setError(e.message); setActionBusy(null); }
  };

  // 📅 Concept-mail aan de planner (draft-only, staat in je eigen Outlook klaar).
  const doPlanner = async (item) => {
    setActionBusy('planner'); setError(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${item.id}/planner-draft`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Planner-concept mislukt');
      setActionMsg({ itemId: item.id, text: `Concept-mail aan ${d.to} staat klaar in Outlook ✔`, link: d.web_link });
    } catch (e) { setError(e.message); } finally { setActionBusy(null); }
  };

  // 📅 Reageren op een Outlook-agenda-uitnodiging: accepteren / onder voorbehoud / weigeren.
  // Zet de afspraak in de agenda en stuurt (optioneel) een reactie naar de organisator.
  const doMeetingRespond = async (item, response) => {
    const cfg = meetingResp[item.id] || {};
    setMeetingResp((m) => ({ ...m, [item.id]: { ...cfg, busy: response } }));
    setError(null);
    const stuurReactie = cfg.sendResponse !== false;
    const reageer = async () => {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${item.id}/meeting-respond`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response, send_response: stuurReactie, comment: cfg.comment || '' }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Reageren op uitnodiging mislukt');
      return d;
    };
    try {
      let d;
      if (stuurReactie) {
        // Er gaat mail naar de organisator: uitgesteld (5 s) met terughaalknop.
        const kort = { accept: 'Acceptatie', tentative: 'Reactie (onder voorbehoud)', decline: 'Afwijzing' };
        const r = await voerUitgesteldUit(`${kort[response] || 'Reactie'} naar ${item.from_name || item.from_email} wordt verzonden…`, reageer, { duur: 5000 });
        if (r.geannuleerd) { setMeetingResp((m) => ({ ...m, [item.id]: { ...(m[item.id] || {}), busy: null } })); return; }
        if (r.fout) throw r.fout;
        d = r.resultaat || {};
      } else {
        d = await reageer();
      }
      const labels = { accept: 'geaccepteerd', tentative: 'onder voorbehoud geaccepteerd', decline: 'geweigerd' };
      if (d.gone) {
        setActionMsg({ itemId: item.id, text: 'Deze uitnodiging was al beantwoord of verplaatst in Outlook.' });
      } else {
        setActionMsg({ itemId: null, text: `Afspraak ${labels[response] || response}${d.sent ? ' — reactie naar organisator verstuurd' : ' (zonder reactie)'} ✔` });
      }
      logAction(item, `meeting_${response}`);
      removeCard(item.id);   // server heeft het item op 'done' gezet
    } catch (e) {
      setError(e.message);
      setMeetingResp((m) => ({ ...m, [item.id]: { ...(m[item.id] || {}), busy: null } }));
    }
  };

  // 👁 Originele mail bekijken. Volgorde: server-route → eigen login → terugzoeken
  // op onderwerp+tijd (voor items met een oud/verlopen mail-id, bv. na verplaatsen).
  const openPreview = async (it) => {
    setPreview({ open: true, loading: true, subject: it.subject, item: it });
    let d = null;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${it.id}/body`);
      if (r.ok) d = await r.json();
    } catch { /* server-route kan falen — probeer hieronder verder */ }
    if (!d?.body && it.message_id) {
      try { d = { ...(d || {}), body: await getMailBody(it.message_id) }; } catch { /* oud id */ }
    }
    if (!d?.body && it.subject) {
      // Laatste redmiddel: mail terugzoeken op onderwerp, dichtstbijzijnd tijdstip wint.
      try {
        const results = await searchMail(it.subject, { top: 10 });
        const ts = (s) => (s ? new Date(s).getTime() : null);
        const ref = ts(it.received_at) ?? ts(`${it.received_at}Z`);
        const best = results
          .filter((m) => (m.subject || '').toLowerCase() === (it.subject || '').toLowerCase())
          .sort((a, b) => Math.abs((ts(a.received) || 0) - ref) - Math.abs((ts(b.received) || 0) - ref))[0];
        if (best) d = { ...(d || {}), body: await getMailBody(best.id) };
      } catch { /* ook niet gevonden */ }
    }
    // Jouw eigen reactie erbij (Joris, 31 aug): via het bewaarde message_id, of
    // — voor oudere kaarten zonder id — terugzoeken op onderwerp + tijdstip.
    let myReply = null;
    if (it.already_replied) {
      try {
        let mid = it.already_replied.message_id;
        if (!mid && it.subject) {
          const ref = new Date(it.already_replied.at).getTime();
          const results = await searchMail(it.subject, { top: 10 });
          const best = results
            .filter((m) => m.received && Math.abs(new Date(m.received).getTime() - ref) < 12 * 3600e3)
            .sort((a, b) => Math.abs(new Date(a.received).getTime() - ref) - Math.abs(new Date(b.received).getTime() - ref))[0];
          if (best) mid = best.id;
        }
        const replyBody = mid ? await getMailBody(mid) : null;
        myReply = { at: it.already_replied.at, body: replyBody || it.already_replied.preview || '' };
      } catch { myReply = { at: it.already_replied.at, body: it.already_replied.preview || '' }; }
    }
    // Alleen bijwerken als de preview nog openstaat — anders heropent een trage
    // fetch de dialoog nadat de gebruiker hem al met Esc had gesloten.
    setPreview((p) => (p?.open ? {
      open: true, loading: false, subject: it.subject, item: it, ...(d || {}), myReply,
      body: d?.body || 'Deze mail is inmiddels verplaatst of verwijderd in Outlook en kan niet meer worden opgehaald. De samenvatting op de kaart blijft gewoon bruikbaar.',
    } : p));
  };

  // ✍️ Concept bewerken: opslaan bewaart het origineel als leersignaal en
  // vervangt de Outlook-draft (oude weg, nieuwe ervoor in de plaats — draft-only).
  const saveConcept = async (it) => {
    const text = (editConcept?.text || '').trim();
    if (!text) return;
    setEditConcept((ec) => ({ ...ec, saving: true }));
    try {
      let newDraftId = null;
      // Alleen vervangen als er al een Outlook-draft staat; anders blijft het
      // concept in de tooling tot je op "Zet in Outlook" drukt.
      if (it.message_id && it.draft_message_id) {
        try {
          await deleteDraft(it.draft_message_id);
          newDraftId = await createReplyDraft(it.message_id, text);
        } catch (draftErr) { console.warn('[Frits] draft vervangen mislukt:', draftErr.message); }
      }
      await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${it.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ concept: text, ...(newDraftId ? { draft_message_id: newDraftId } : {}) }),
      });
      setItems((prev) => prev.map((i) => i.id === it.id
        ? { ...i, concept: text, draft_created: i.draft_created || !!newDraftId, draft_message_id: newDraftId || i.draft_message_id }
        : i));
      setEditConcept(null);
      setActionMsg({ itemId: it.id, text: newDraftId ? 'Concept aangepast — nieuwe draft staat in Outlook ✔' : 'Concept aangepast ✔' });
      return { ...it, concept: text, draft_created: it.draft_created || !!newDraftId, draft_message_id: newDraftId || it.draft_message_id };
    } catch (e) {
      setError(`Concept opslaan mislukt: ${e.message}`);
      setEditConcept((ec) => (ec ? { ...ec, saving: false } : ec));
      return null;
    }
  };

  // ✏️→📤 Interne mail: opslaan en direct versturen in één klik.
  const saveAndSend = async (it) => {
    const saved = await saveConcept(it);
    if (saved?.draft_message_id) await sendConcept(saved);
  };

  // 🧾 Factureren gebeurt ALTIJD vanuit de Gripp-opdracht (Joris, 31 aug) —
  // geen losse concept-facturen meer. Zonder projectkoppeling eerst koppelen.
  const openInvoice = async (it) => {
    const pnr = it.project_match?.project_number;
    if (pnr) {
      try {
        const r = await authFetch(`${AI_ENGINE_URL}/api/v1/gripp/project-id?number=${encodeURIComponent(pnr)}`);
        const d = r?.ok ? await r.json() : null;
        if (d?.gripp_project_id) {
          window.open(buildGrippProjectUrl(d.gripp_project_id), '_blank', 'noopener');
          logAction(it, 'action_invoice');
          return;
        }
      } catch { /* valt door naar de melding hieronder */ }
      setError(`Geen Gripp-opdracht gevonden bij projectnummer ${pnr} — is de offerte al omgezet naar een opdracht?`);
      return;
    }
    setError('Koppel de mail eerst aan een project — factureren gaat altijd vanuit de Gripp-opdracht.');
    setLinkDlg({ item: it, q: '', opts: [], loading: false });
  };

  // 🎓 "Leer Frits": feedback wordt een triage-regel die in elke volgende run meegaat.
  const LESSON_CHOICES = [
    { key: 'fyi', label: 'Dit is ter info — geen antwoord nodig', lesson: 'Mails zoals deze zijn alleen ter info voor mij: geen antwoord nodig, geen concept, prioriteit laag.' },
    { key: 'cc', label: 'Ik sta in CC / iemand anders is aan zet', lesson: 'Als ik alleen in CC sta en de mail aan iemand anders is gericht: geen concept-antwoord voor mij maken, markeer als fyi.' },
    { key: 'prio-hoog', label: 'Prioriteit was te hoog', lesson: 'Dit soort mails kreeg een te hoge prioriteit; schaal vergelijkbare mails lager in.' },
    { key: 'prio-laag', label: 'Prioriteit was te laag', lesson: 'Dit soort mails kreeg een te lage prioriteit; schaal vergelijkbare mails hoger in.' },
  ];
  const submitLesson = async () => {
    const choice = LESSON_CHOICES.find((c) => c.key === lessonDlg.choice);
    const lesson = [choice?.lesson, (lessonDlg.text || '').trim()].filter(Boolean).join(' ');
    if (!lesson) return;
    setLessonDlg((d) => ({ ...d, busy: true }));
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${lessonDlg.item.id}/lesson`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lesson }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Opslaan mislukt');
      setLessonDlg(null);
      setActionMsg({ itemId: lessonDlg.item.id, text: `Frits heeft het onthouden (${d.lessons_count} geleerde regels) ✔` });
    } catch (e) {
      setError(e.message);
      setLessonDlg((d) => (d ? { ...d, busy: false } : d));
    }
  };

  const ACTION_BTN = {
    todo: { icon: <AddTaskIcon />, run: (item, action) => openTaskDlg(item, action), fallback: 'Taak aanmaken' },
    offerte: { icon: <ReceiptLongIcon />, run: doOfferte, fallback: 'Start offerte uit deze mail' },
    planner: { icon: <EventIcon />, run: doPlanner, fallback: 'Concept naar planner' },
    factuur: { icon: <RequestQuoteIcon />, run: (item) => openInvoice(item), fallback: 'Open opdracht in Gripp' },
  };

  // Sneltoetsen: Enter = klaar & volgende · S = overslaan
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      // ← of Ctrl/Cmd+Z: laatste afhandeling terugdraaien — werkt ook als de lijst leeg is.
      if (((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') || e.key === 'ArrowLeft') {
        if (lastAction) { e.preventDefault(); undoLast(); }
        return;
      }
      const item = items?.[idx];
      if (!item || statusFilter !== 'open' || item.verzendend) return;
      // Esc sluit de concept-editor (dialogen sluiten zichzelf al op Esc).
      if (e.key === 'Escape' && editConcept) { e.preventDefault(); setEditConcept(null); return; }
      // Geen kaart-sneltoetsen terwijl een dialoog of editor openstaat. LET OP:
      // alleen een editor op de HUIDIGE kaart telt — een achtergebleven editor
      // van een al weggewerkte kaart blokkeerde anders onzichtbaar alle toetsen.
      const editorOnThisCard = editConcept && editConcept.itemId === item.id;
      if (editConcept && !editorOnThisCard) setEditConcept(null);
      if (preview?.open || lessonDlg || taskDlg || delegateDlg || moveDlg || meerAnchor || editorOnThisCard) return;
      if (e.key === 'Enter') { e.preventDefault(); handle(item, 'done'); }
      if (e.key === '?') { e.preventDefault(); setShowShortcuts(true); return; }
      const k = e.key.toLowerCase();
      if (k === 's') { e.preventDefault(); handle(item, 'skipped'); }
      if (k === 'a') { e.preventDefault(); archive(item); }
      if (k === 'x' || e.key === 'Delete') { e.preventDefault(); verwijderMail(item); }
      if (k === 'b') { e.preventDefault(); openPreview(item); }
      if (k === 'v') { e.preventDefault(); openMoveDlg(item); }
      if (k === 'e') { e.preventDefault(); setEditConcept({ itemId: item.id, text: item.concept || '' }); }
      if (k === 'c') { e.preventDefault(); chatInputRef.current?.focus(); }
      if (k === 'm') { e.preventDefault(); if (meerBtnRef.current) setMeerAnchor(meerBtnRef.current); }
      if (k === 'd') { e.preventDefault(); openDelegateDlg(item); }
      if (k === 't') { e.preventDefault(); openTaskDlg(item, (item.suggested_actions || []).find((a) => a.type === 'todo') || null); }
      if (k === 'l') { e.preventDefault(); setLessonDlg({ item, choice: null, text: '' }); }
      if (k === 'o' && followUpTarget(item)) { e.preventDefault(); followUp(item); }
      if (k === 'z' && (item.concept || item.draft_created) && isInternalAddr(item.from_email)) { e.preventDefault(); sendConcept(item); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // 🔄 Live-concept: bij het tonen van een kaart de actuele draft uit Outlook
  // ophalen — ook als je het concept dáár bewerkte of zelf een draft begon.
  const draftCheckedRef = useRef(new Set());
  useEffect(() => {
    const it = items?.[idx];
    if (!it || statusFilter !== 'open' || !it.message_id) return;
    if (draftCheckedRef.current.has(it.id)) return;
    draftCheckedRef.current.add(it.id);
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${it.id}/draft`);
        if (!r.ok) return;
        const d = await r.json();
        // Mail is in Outlook verplaatst/verwijderd → server heeft 'm afgerond; kaart weg.
        if (d.gone) { removeCard(it.id); return; }
        setItems((prev) => prev.map((i) => {
          if (i.id !== it.id) return i;
          // Server kan live "al beantwoord" gedetecteerd hebben — samenvatting + badge mee-verversen.
          const replied = d.item?.already_replied ? {
            already_replied: d.item.already_replied,
            summary: d.item.summary || i.summary,
            needs_reply: false,
          } : {};
          if (d.exists) {
            return {
              ...i, ...replied, concept: d.concept || i.concept, draft_created: true,
              draft_message_id: d.item?.draft_message_id || i.draft_message_id,
            };
          }
          const base = i.draft_created ? { ...i, draft_created: false, draft_message_id: null } : i;
          return Object.keys(replied).length ? { ...base, ...replied } : base;
        }));
      } catch { /* stil — kaart blijft de opgeslagen versie tonen */ }
    })();
  });

  const item = items?.[idx];
  const prio = PRIO_STYLE[item?.priority] || PRIO_STYLE.normaal;


  return (
    <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', px: { xs: 1.25, sm: 3, md: 5 }, pt: { xs: 2, md: 5 }, pb: 6 }}>
      <PageHeader
        icon="mark_email_unread"
        title="Frits"
        subtitle={isMobile ? undefined : "Frits neemt elke ochtend om 07:30 je inbox door: prioriteit, samenvatting en een concept-antwoord in jouw stijl klaar in Outlook. Versturen doe je altijd zelf."}
        rightActions={isMobile ? undefined : (
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Hoe werkt Frits? — korte uitleg">
              <IconButton size="small" onClick={() => setShowOnboarding(true)}><HelpOutlineIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Mijn schrijfstijl (zo schrijft Frits namens jou)">
              <IconButton size="small" onClick={() => setShowTone(true)}><RecordVoiceOverOutlinedIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Mijn e-mailhandtekening (onder Frits-concepten)">
              <IconButton size="small" onClick={() => setShowSignature(true)}><DriveFileRenameOutlineIcon fontSize="small" /></IconButton>
            </Tooltip>
            {pushState === 'off' && (
              <Tooltip title="Ochtend-melding aanzetten: push zodra Frits om 07:30 je triage klaar heeft">
                <IconButton size="small" onClick={enablePush}><NotificationsActiveOutlinedIcon fontSize="small" /></IconButton>
              </Tooltip>
            )}
            <ToggleButtonGroup size="small" exclusive value={statusFilter}
              onChange={(_e, v) => { if (v) { setStatusFilter(v); setItems(null); } }}
              sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.25, fontSize: 12, textTransform: 'none', fontWeight: 700 } }}>
              <ToggleButton value="open">Open</ToggleButton>
              <ToggleButton value="done">Afgehandeld</ToggleButton>
            </ToggleButtonGroup>
            <Button variant="contained" size="small" startIcon={running ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
              onClick={runNow} disabled={running} sx={{ textTransform: 'none', fontWeight: 700 }}>
              {running ? 'Mail checken…' : 'Check nu'}
            </Button>
          </Stack>
        )}
      />

      {/* Mobiel: knoppen op een eigen rij ONDER de titel — naast de titel plet
          de tekst tot een woord-per-regel kolom (screenshot Joris 6 juli). */}
      {isMobile && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <ToggleButtonGroup size="small" exclusive value={statusFilter}
            onChange={(_e, v) => { if (v) { setStatusFilter(v); setItems(null); } }}
            sx={{ '& .MuiToggleButton-root': { py: 0.5, px: 1.5, fontSize: 13, textTransform: 'none', fontWeight: 700 } }}>
            <ToggleButton value="open">Open</ToggleButton>
            <ToggleButton value="done">Afgehandeld</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ flex: 1 }} />
          {pushState === 'off' && (
            <IconButton size="small" onClick={enablePush} aria-label="Ochtend-melding aanzetten">
              <NotificationsActiveOutlinedIcon fontSize="small" />
            </IconButton>
          )}
          <Button variant="contained" size="small" startIcon={running ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
            onClick={runNow} disabled={running} sx={{ textTransform: 'none', fontWeight: 700, minHeight: 40 }}>
            {running ? 'Checken…' : 'Check nu'}
          </Button>
        </Stack>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {actionMsg?.itemId === null && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionMsg(null)}>{actionMsg.text}</Alert>
      )}
      {infoMsg && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfoMsg(null)}>{infoMsg}</Alert>}
      {notEnabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Frits staat nog niet aan voor jouw account — toegang gaat per mailbox
          (beveiligings-scope). Vraag Joris om je toe te voegen.
        </Alert>
      )}

      {items === null && <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}

      {items !== null && items.length === 0 && !notEnabled && (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 40, mb: 1 }}>🎉</Typography>
          <Typography fontWeight={700}>{statusFilter === 'open' ? 'Alles getriageerd!' : 'Nog niets afgehandeld.'}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {statusFilter === 'open' ? 'Frits doet elke ochtend om 07:30 zijn ronde — of klik "Check nu".' : ''}
          </Typography>
        </Paper>
      )}

      {offerRem.list.length > 0 && statusFilter === 'open' && (
        <Paper elevation={0} sx={{ mb: 2, p: 1.5, borderRadius: 2, border: '1px solid #fde68a', bgcolor: '#fffbeb' }}>
          <Stack direction="row" alignItems="center" sx={{ cursor: 'pointer' }}
            onClick={() => setOfferRem((o) => ({ ...o, open: !o.open }))}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#92400e' }}>
              🔔 Offerte-reminders ({offerRem.list.length}) — langer dan een week open
            </Typography>
            <Box sx={{ flex: 1 }} />
            {offerRem.open ? <ExpandMoreIcon sx={{ fontSize: 18, color: '#92400e' }} /> : <ChevronRightIcon sx={{ fontSize: 18, color: '#92400e' }} />}
          </Stack>
          <Collapse in={offerRem.open}>
            <Stack spacing={0.75} sx={{ mt: 1 }}>
              {offerRem.list.map((rem) => (
                <Stack key={rem.number} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 0 }}>
                    {rem.number} — {rem.subject || '(zonder onderwerp)'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {rem.company_name}{rem.contact_name ? ` · ${rem.contact_name}` : ''} · {rem.days_open} dagen
                    {rem.total_excl ? ` · €${Math.round(rem.total_excl / 100).toLocaleString('nl-NL')}` : ''}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Button size="small" variant="outlined" disabled={offerRem.busy === rem.number}
                    onClick={() => maakOfferReminder(rem)}
                    startIcon={offerRem.busy === rem.number ? <CircularProgress size={12} /> : <DraftsIcon sx={{ fontSize: 14 }} />}
                    sx={{ textTransform: 'none', fontWeight: 700, py: 0, fontSize: 12 }}>
                    Reminder-concept
                  </Button>
                  <Button size="small" variant="text" component="a" target="_blank" rel="noopener"
                    href={buildGrippOfferUrl(rem.gripp_id)}
                    sx={{ textTransform: 'none', py: 0, fontSize: 12, color: '#64748b' }}>
                    Gripp ↗
                  </Button>
                  <Tooltip title="Een week stil — daarna komt hij vanzelf terug">
                    <IconButton size="small" onClick={() => snoozeOfferReminder(rem)} sx={{ p: 0.25 }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          </Collapse>
        </Paper>
      )}

      {item && (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {undoStack.length > 0 && (
                <Tooltip title={`Vorige mail terughalen — laatste afhandeling ongedaan maken (← of ⌘Z), nog ${undoStack.length} terug`}>
                  <IconButton size="small" onClick={undoLast} disabled={undoBusy} sx={{ p: 0.25 }}>
                    {undoBusy ? <CircularProgress size={14} /> : <ReplayIcon sx={{ fontSize: 16, transform: 'scaleX(-1)' }} />}
                  </IconButton>
                </Tooltip>
              )}
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>
                Mail {idx + 1} van {items.length}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.disabled">
              {isMobile ? 'Swipe → klaar · ← overslaan' : 'Enter = klaar · S = skip · A = archief · C = chat · ← = terug · druk ? voor alle sneltoetsen'}
            </Typography>
          </Stack>

          {/* key={item.id}: bij kaart-wissel de hele subtree vers opbouwen — hergebruik
              van de oude kaart (met open tooltip/menu/transition) gaf op mobiel soms
              een DOM NotFoundError ("Er ging iets mis", weg na refresh). */}
          <Paper key={item.id} variant="outlined" sx={{ overflow: 'hidden', borderLeft: '4px solid', borderLeftColor: prio.color,
            ...(item.verzendend ? { opacity: 0.55, pointerEvents: 'none' } : {}) }}
            onTouchStart={(e) => { touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; }}
            onTouchEnd={(e) => {
              // Swipe-triage op mobiel: rechts = klaar, links = overslaan.
              // Alleen bij overwegend horizontale beweging (geen scroll) en niet tijdens bewerken.
              const s = touchStart.current; touchStart.current = null;
              if (!s || statusFilter !== 'open' || editConcept) return;
              const dx = e.changedTouches[0].clientX - s.x;
              const dy = e.changedTouches[0].clientY - s.y;
              if (Math.abs(dx) > 90 && Math.abs(dy) < 50) handle(item, dx > 0 ? 'done' : 'skipped');
            }}>
            {/* Kop */}
            <Box sx={{ px: { xs: 1.75, sm: 3 }, pt: { xs: 1.75, sm: 2.5 }, pb: 1.5, borderBottom: '1px solid #f1f5f9' }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
                <Chip size="small" label={prio.label} sx={{ fontWeight: 800, bgcolor: prio.bg, color: prio.color }} />
                <Chip size="small" label={CAT_LABEL[item.category] || item.category} variant="outlined" sx={{ fontWeight: 600 }} />
                {item.already_replied && (
                  <Tooltip title={`Jouw reactie (${(item.already_replied.at || '').slice(0, 10)}): “${item.already_replied.preview || ''}”`}>
                    <Chip size="small" icon={<CheckIcon sx={{ fontSize: 14 }} />} label="Al beantwoord door jou"
                      sx={{ fontWeight: 800, bgcolor: '#dcfce7', color: '#166534' }} />
                  </Tooltip>
                )}
                {item.draft_created && !item.already_replied && (
                  <Chip size="small" icon={<DraftsIcon sx={{ fontSize: 14 }} />} label="Concept staat klaar in Outlook"
                    sx={{ fontWeight: 700, bgcolor: '#dcfce7', color: '#15803d' }} />
                )}
                <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                  {item.received_at ? new Date(item.received_at).toLocaleString('nl-NL', { weekday: 'short', hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' }) : ''}
                </Typography>
                {/* Compacte actie- en afhandel-knoppen bovenin — geen scrollen
                    naar beneden nodig; zelfde handlers als de rij onderaan. */}
                <Stack direction="row" spacing={0.25} alignItems="center">
                  <Tooltip title="Originele mail bekijken (B)">
                    <IconButton size="small" onClick={() => openPreview(item)}><VisibilityOutlinedIcon sx={{ fontSize: 17 }} /></IconButton>
                  </Tooltip>
                  <Tooltip title="Zet als todo in Microsoft To-Do (T)">
                    <IconButton size="small" onClick={() => openTaskDlg(item, (item.suggested_actions || []).find((a) => a.type === 'todo') || null, 'todo')}><AddTaskIcon sx={{ fontSize: 17 }} /></IconButton>
                  </Tooltip>
                  <Tooltip title="Delegeren naar een collega (D)">
                    <IconButton size="small" onClick={() => openDelegateDlg(item)}><ForwardToInboxIcon sx={{ fontSize: 17 }} /></IconButton>
                  </Tooltip>
                  <Tooltip title="Leer Frits (L)">
                    <IconButton size="small" onClick={() => setLessonDlg({ item, choice: null, text: '' })}><SchoolOutlinedIcon sx={{ fontSize: 17 }} /></IconButton>
                  </Tooltip>
                  {item.web_link && (
                    <Tooltip title="Open in Outlook">
                      <IconButton size="small" href={item.web_link} target="_blank" rel="noopener"><OpenInNewIcon sx={{ fontSize: 17 }} /></IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Naar map (V)">
                    <IconButton size="small" onClick={() => openMoveDlg(item)}><DriveFileMoveOutlinedIcon sx={{ fontSize: 17 }} /></IconButton>
                  </Tooltip>
                  <Box sx={{ width: 1, alignSelf: 'stretch', my: 0.5, borderLeft: '1px solid #e2e8f0', mx: 0.5 }} />
                  <Tooltip title="Klaar & volgende (Enter)">
                    <IconButton size="small" color="success" onClick={() => handle(item, 'done')}><CheckIcon sx={{ fontSize: 18 }} /></IconButton>
                  </Tooltip>
                  <Tooltip title="Overslaan (S) — tijdelijk: komt terug bij de volgende run. Archiveer (A) om 'm definitief weg te halen.">
                    <IconButton size="small" onClick={() => handle(item, 'skipped')}><SkipNextIcon sx={{ fontSize: 18 }} /></IconButton>
                  </Tooltip>
                  <Tooltip title="Archiveren (A)">
                    <IconButton size="small" onClick={() => archive(item)}><ArchiveOutlinedIcon sx={{ fontSize: 18 }} /></IconButton>
                  </Tooltip>
                  <Tooltip title="Verwijderen — naar de Outlook-prullenbak (X)">
                    <IconButton size="small" onClick={() => verwijderMail(item)}><DeleteOutlineIcon sx={{ fontSize: 18 }} /></IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
              <Typography sx={{ fontWeight: 800, fontSize: 17, lineHeight: 1.3 }}>{item.subject || '(geen onderwerp)'}</Typography>
              <Typography variant="body2" color="text.secondary">{item.from_name} &lt;{item.from_email}&gt;</Typography>
              {((item.to_recipients || []).length > 0 || (item.cc_recipients || []).length > 0) && (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.35 }}>
                  <Typography variant="caption" color="text.disabled">
                    {(item.to_recipients || []).length > 0 && <>Aan: {addrList(item.to_recipients)}</>}
                    {(item.cc_recipients || []).length > 0 && <>{(item.to_recipients || []).length > 0 ? ' · ' : ''}CC: {addrList(item.cc_recipients)}</>}
                  </Typography>
                  {(item.cc_recipients || []).some(isMe) && !(item.to_recipients || []).some(isMe) && (
                    <Chip size="small" label="Jij staat in CC" sx={{ height: 18, fontSize: 10.5, fontWeight: 800, bgcolor: '#fef3c7', color: '#92400e' }} />
                  )}
                </Stack>
              )}
            </Box>

            {/* Project-koppeling: match tonen (klik = wijzigen), of aanbieden te koppelen */}
            <Box sx={{ px: { xs: 1.75, sm: 3 }, pt: 1 }}>
              {item.project_match?.project_name ? (
                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip size="small" clickable
                    onClick={() => {
                      if (item.project_match.airtable_project_id) {
                        window.open(`/projects/${item.project_match.airtable_project_id}`, '_blank', 'noopener');
                      } else {
                        setLinkDlg({ item, q: '', opts: [], loading: false });
                      }
                    }}
                    label={`📁 ${item.project_match.project_number ? `${item.project_match.project_number} — ` : ''}${item.project_match.project_name}${
                      item.project_match.match_source !== 'onthouden' && item.project_match.confidence < 0.6 ? ' (?)' : ''}`}
                    title={item.project_match.airtable_project_id
                      ? 'Open de projectkaart in een nieuw tabblad.'
                      : 'Klik om een project te kiezen.'}
                    sx={{ fontWeight: 700,
                      bgcolor: item.project_match.match_source === 'onthouden' ? '#dcfce7' : item.project_match.confidence >= 0.6 ? '#e0f2fe' : '#f1f5f9',
                      color: item.project_match.match_source === 'onthouden' ? '#166534' : item.project_match.confidence >= 0.6 ? '#075985' : '#475569', maxWidth: 380 }} />
                  {item.project_match.match_source === 'onthouden' ? (
                    <Chip size="small" label="✓ vastgelegd" sx={{ height: 18, fontSize: 10, bgcolor: '#dcfce7', color: '#166534' }} />
                  ) : item.project_match.airtable_project_id ? (
                    <Button size="small" variant="text"
                      title={`Waarschijnlijke match op ${
                        { project_number: 'projectnummer', contactpersoon: 'contactpersoon', klant: 'klant' }[item.project_match.match_source] || 'context'
                      } (~${Math.round((item.project_match.confidence || 0) * 100)}%). Klik om vast te leggen voor deze thread + contactpersoon.`}
                      onClick={() => linkProject(item, { airtable_project_id: item.project_match.airtable_project_id })}
                      sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0, px: 0.75, py: 0, fontSize: 12, color: '#166534' }}>
                      ✓ Vastleggen
                    </Button>
                  ) : null}
                  <IconButton size="small" title="Koppeling wijzigen" sx={{ p: 0.25 }}
                    onClick={() => setLinkDlg({ item, q: '', opts: [], loading: false })}>
                    <EditOutlinedIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                  <IconButton size="small" title="Koppeling loskoppelen" onClick={() => unlinkProject(item)} sx={{ p: 0.25 }}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Stack>
              ) : (
                <Button size="small" variant="text" startIcon={<span>📁</span>}
                  onClick={() => setLinkDlg({ item, q: '', opts: [], loading: false })}
                  sx={{ textTransform: 'none', fontWeight: 700, color: '#64748b' }}>
                  Koppel aan project
                </Button>
              )}
              {/* Gaat de mail over een offerte? Toon de Gripp-match + bij een
                  akkoord het voorstel om hem om te zetten (Joris, 30 aug). */}
              {(offerMatch[item.id]?.offers || []).map((o) => (
                <Stack key={o.gripp_id} direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                  <Chip size="small" component="a" href={buildGrippOfferUrl(o.gripp_id)} target="_blank" rel="noopener" clickable
                    label={`📄 Offerte ${o.number}${o.subject ? ` — ${o.subject}` : ''} · ${o.phase_name || 'onbekende fase'}`}
                    sx={{ fontWeight: 700, bgcolor: o.phase === 3 ? '#dcfce7' : '#fef9c3', color: o.phase === 3 ? '#166534' : '#854d0e', maxWidth: 420 }} />
                  {heeftAkkoordHint(item) && o.phase !== 3 && (
                    <Button size="small" variant="outlined" color="success" disabled={offerMatch[item.id]?.busy}
                      onClick={() => accepteerOfferte(item, o)}
                      sx={{ textTransform: 'none', fontWeight: 700, py: 0, fontSize: 12 }}>
                      {offerMatch[item.id]?.busy ? <CircularProgress size={13} /> : '✅ Zet op Geaccepteerd → opdracht maken'}
                    </Button>
                  )}
                </Stack>
              ))}
            </Box>

            {/* Samenvatting + prio-reden */}
            <Box sx={{ px: { xs: 1.75, sm: 3 }, py: 2 }}>
              {item.already_replied && (
                <Alert severity="success" onClick={() => openPreview(item)}
                  sx={{ mb: 1, py: 0.25, cursor: 'pointer', '&:hover': { bgcolor: '#dcfce7' } }}>
                  Je hebt hierop al gereageerd ({(item.already_replied.at || '').slice(0, 10)}): “{(item.already_replied.preview || '').slice(0, 140)}” — <b>klik om je antwoord te bekijken</b>
                </Alert>
              )}
              <Typography variant="body1" sx={{ lineHeight: 1.7 }}>{item.summary}</Typography>
              {item.prio_reason && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Prioriteit: {item.prio_reason}
                </Typography>
              )}

              {/* Automatische scope-waarschuwing uit de triage (offerte-vergelijking) */}
              {item.scope_warning?.scope_change && (
                <Alert severity={item.scope_warning.risico === 'hoog' ? 'error' : 'warning'} sx={{ mt: 1 }}>
                  <b>Let op — mogelijke scope-wijziging t.o.v. de offerte</b> (risico: {item.scope_warning.risico}). {item.scope_warning.toelichting}
                  {(item.scope_warning.punten || []).length > 0 && (
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {item.scope_warning.punten.map((p, i) => <li key={i}><Typography variant="caption">{p}</Typography></li>)}
                    </Box>
                  )}
                </Alert>
              )}

              {/* Agenda-uitnodiging — geen tekst-antwoord forceren; accepteren/weigeren */}
              {item.is_meeting_request && (() => {
                const mr = meetingResp[item.id] || {};
                const sendResp = mr.sendResponse !== false;
                return (
                  <Box sx={{ mt: 2, p: 2, borderRadius: 1.5, bgcolor: '#eef2ff', border: '1px solid #c7d2fe' }}>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#4338ca', display: 'block', mb: 0.5 }}>
                      📅 Agenda-uitnodiging — hier hoeft geen tekst-antwoord op
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Reageren zet de afspraak in je agenda. Check hieronder eerst je beschikbaarheid als je wilt.
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                      <Button size="small" variant="contained" color="success" disabled={!!mr.busy}
                        startIcon={mr.busy === 'accept' ? <CircularProgress size={13} color="inherit" /> : null}
                        onClick={() => doMeetingRespond(item, 'accept')}
                        sx={{ textTransform: 'none', fontWeight: 800 }}>
                        ✅ Accepteren
                      </Button>
                      <Button size="small" variant="outlined" disabled={!!mr.busy}
                        startIcon={mr.busy === 'tentative' ? <CircularProgress size={13} /> : null}
                        onClick={() => doMeetingRespond(item, 'tentative')}
                        sx={{ textTransform: 'none', fontWeight: 700, borderColor: '#f59e0b', color: '#b45309' }}>
                        ❓ Onder voorbehoud
                      </Button>
                      <Button size="small" variant="outlined" color="error" disabled={!!mr.busy}
                        startIcon={mr.busy === 'decline' ? <CircularProgress size={13} /> : null}
                        onClick={() => doMeetingRespond(item, 'decline')}
                        sx={{ textTransform: 'none', fontWeight: 700 }}>
                        ✖ Weigeren
                      </Button>
                    </Stack>
                    <TextField fullWidth size="small" placeholder="Optioneel bericht aan de organisator…"
                      value={mr.comment || ''}
                      onChange={(e) => setMeetingResp((m) => ({ ...m, [item.id]: { ...(m[item.id] || {}), comment: e.target.value } }))}
                      sx={{ bgcolor: '#fff', mb: 0.5 }} />
                    <FormControlLabel
                      control={<Checkbox size="small" checked={sendResp}
                        onChange={(e) => setMeetingResp((m) => ({ ...m, [item.id]: { ...(m[item.id] || {}), sendResponse: e.target.checked } }))} />}
                      label={<Typography variant="caption" color="text.secondary">Reactie naar de organisator sturen</Typography>} />
                  </Box>
                );
              })()}

              {/* Beschikbaarheidscheck — boven de draft: eerst zien of het kan, dan het antwoord */}
              {item.category === 'planning' && (() => {
                // Compact: standaard ingeklapt — pas open na een klik of een verse check.
                const availDicht = availCollapsed[item.id] ?? !(avail.itemId === item.id && !!avail.result);
                return (
                <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <Stack direction="row" alignItems="center" spacing={0.5} onClick={() => setAvailCollapsed((c) => ({ ...c, [item.id]: !availDicht }))}
                    sx={{ cursor: 'pointer', mb: availDicht ? 0 : 1 }}>
                    {availDicht ? <ChevronRightIcon sx={{ fontSize: 16, color: '#0f766e' }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: '#0f766e' }} />}
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#0f766e' }}>📅 Beschikbaarheid checken</Typography>
                    {/* Compacte verdict-samenvatting als ingeklapt */}
                    {availDicht && avail.itemId === item.id && avail.result && !avail.result.error && (() => {
                      const v = avail.result.verdict || {};
                      const t = v.all_free ? { c: '#15803d', l: `✅ ${avail.start}–${avail.end} lukt` }
                        : v.external_block ? { c: '#b91c1c', l: '⛔ niet mogelijk' }
                        : v.only_internal_conflicts ? { c: '#92400e', l: '⚠️ mits verschuiven' }
                        : { c: '#64748b', l: 'geen agenda-data' };
                      return <Typography variant="caption" sx={{ ml: 'auto', fontWeight: 700, color: t.c }}>{t.l}</Typography>;
                    })()}
                  </Stack>
                  <Collapse in={!availDicht}>
                  {(() => {
                    const sd = slotsByItem[item.id];
                    if (!sd) return null;
                    if (sd.loading) return <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>Voorstellen uit de mail halen…</Typography>;
                    if (!sd.list.length) return null;
                    return (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mb: 0.25 }}>Voorstellen uit de mail (klik om in te vullen):</Typography>
                        {sd.list.map((s, i) => {
                          const sel = avail.itemId === item.id && avail.date === s.date && avail.start === s.start_time;
                          return (
                            <Chip key={i} size="small" clickable
                              label={`${s.label || s.date}${s.approx ? ' ~' : ''}`}
                              onClick={() => setAvail((a) => ({ ...a, itemId: item.id, date: s.date, start: s.start_time, end: endAfterStart(s.start_time, s.end_time), result: null }))}
                              sx={{ fontWeight: 600, bgcolor: sel ? '#0f766e' : '#e2e8f0', color: sel ? '#fff' : '#334155' }} />
                          );
                        })}
                      </Stack>
                    );
                  })()}
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    <TextField type="date" size="small" label="Datum" InputLabelProps={{ shrink: true }}
                      value={avail.date} onChange={(e) => setAvail((a) => ({ ...a, date: e.target.value }))} sx={{ width: 165 }} />
                    <TextField select size="small" label="Van" value={avail.start}
                      onChange={(e) => setAvail((a) => ({ ...a, start: e.target.value }))} sx={{ width: 100 }}
                      SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 280 } } } }}>
                      {QUARTERS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="Tot" value={avail.end}
                      onChange={(e) => setAvail((a) => ({ ...a, end: e.target.value }))} sx={{ width: 100 }}
                      SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 280 } } } }}>
                      {QUARTERS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </TextField>
                    <Button size="small" variant="contained" disabled={avail.loading || !avail.date}
                      startIcon={avail.loading && avail.itemId === item.id ? <CircularProgress size={13} color="inherit" /> : null}
                      onClick={() => runAvailability(item)} sx={{ textTransform: 'none', fontWeight: 700 }}>
                      Check
                    </Button>
                    <Button size="small" variant="outlined" disabled={findingSlot || !avail.date}
                      startIcon={findingSlot ? <CircularProgress size={13} color="inherit" /> : null}
                      onClick={() => findSlot(item)} sx={{ textTransform: 'none', fontWeight: 700 }}>
                      🔍 Vind een tijd die past
                    </Button>
                    {avail.itemId === item.id && avail.result && !avail.result.error && (
                      <Button size="small" variant="outlined" onClick={() => setVisualOpen(true)}
                        sx={{ textTransform: 'none', fontWeight: 700 }}>
                        📊 Agenda-weergave
                      </Button>
                    )}
                  </Stack>
                  {avail.itemId === item.id && avail.result && (
                    avail.result.error ? (
                      <Alert severity="error" sx={{ mt: 0.5 }}>{avail.result.error}</Alert>
                    ) : (
                      <Box sx={{ mt: 0.5 }}>
                        {(() => {
                          const v = avail.result.verdict || {};
                          const al = v.already_scheduled;
                          const sev = al ? { c: '#dcfce7', t: '#15803d', label: `📌 Staat al ingepland: ${al.start ? `${al.start}–${al.end} ` : ''}"${al.subject}"` }
                            : v.all_free ? { c: '#dcfce7', t: '#15803d', label: '✅ Iedereen beschikbaar' }
                            : v.external_block ? { c: '#fee2e2', t: '#b91c1c', label: '⛔ Niet mogelijk — extern conflict of draaidag' }
                            : v.only_internal_conflicts ? { c: '#fef3c7', t: '#92400e', label: '⚠️ Kan — mits interne afspraken verschuiven' }
                            : { c: '#f1f5f9', t: '#475569', label: 'Geen agenda-data beschikbaar' };
                          return (
                            <Box sx={{ px: 1.25, py: 0.75, borderRadius: 1, bgcolor: sev.c, color: sev.t, fontWeight: 800, fontSize: 13, mb: 1 }}>
                              {sev.label}
                            </Box>
                          );
                        })()}
                        <Stack spacing={0.5}>
                          {(avail.result.attendees || []).map((att) => (
                            <Box key={att.email} sx={{ fontSize: 12.5 }}>
                              <Box component="span" sx={{ fontWeight: 700 }}>{att.email.split('@')[0]}</Box>
                              {!att.accessible ? (
                                <Box component="span" sx={{ color: '#94a3b8' }}> — agenda niet inzichtelijk</Box>
                              ) : att.available ? (
                                <Box component="span" sx={{ color: '#15803d' }}> — vrij</Box>
                              ) : null}
                              {(att.conflicts || []).map((c, i) => (
                                <Box key={i} sx={{ pl: 2, color: c.same_meeting ? '#15803d' : c.kind === 'internal' ? '#92400e' : '#b91c1c' }}>
                                  • {c.all_day ? '' : `${c.start}-${c.end} `}{c.subject}
                                  {c.same_meeting ? ' ✓ dit ís deze afspraak' : c.kind === 'internal' ? ' (intern — verschuifbaar)' : c.kind === 'draaidag' ? ' (draaidag)' : ' (extern)'}
                                  {!c.same_meeting && c.kind !== 'draaidag' && (
                                    <Button size="small" variant="text" disableRipple
                                      disabled={reschedule.loading && reschedule.key === `${item.id}:${att.email}`}
                                      onClick={() => runReschedule(item, att, c)}
                                      sx={{ ml: 0.5, textTransform: 'none', minWidth: 0, p: 0, fontSize: 11, fontWeight: 700, verticalAlign: 'baseline' }}>
                                      ✉️ vraag {att.email.split('@')[0]} te verzetten
                                    </Button>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          ))}
                        </Stack>
                        <Button size="small" variant="contained" color="success" disabled={applyingSlot}
                          startIcon={applyingSlot ? <CircularProgress size={13} color="inherit" /> : null}
                          onClick={() => applySlot(item)}
                          sx={{ mt: 1, textTransform: 'none', fontWeight: 700 }}>
                          ✅ Zet dit tijdstip in het antwoord
                        </Button>
                        {reschedule.key && reschedule.key.startsWith(`${item.id}:`) && (reschedule.link || reschedule.error || reschedule.loading) && (
                          <Alert severity={reschedule.error ? 'error' : 'success'} sx={{ mt: 1 }} onClose={() => setReschedule({ key: null, loading: false, link: null, error: null })}>
                            {reschedule.loading ? 'Concept schrijven…' : reschedule.error ? reschedule.error : (
                              <>Concept staat klaar in Outlook — <Link href={reschedule.link} target="_blank" rel="noopener" sx={{ fontWeight: 700 }}>open</Link></>
                            )}
                          </Alert>
                        )}
                      </Box>
                    )
                  )}
                  </Collapse>
                </Box>
                );
              })()}

              {/* Concept-antwoord — bewerkbaar; opslaan ververst de Outlook-draft.
                  Ook zonder AI-concept te openen via "Antwoord schrijven". */}
              {(item.concept || editConcept?.itemId === item.id) && (
                <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 1.5, bgcolor: '#f8faff', border: '1px solid #e0e7ff' }}>
                  <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#4338ca' }}>
                      {item.concept
                        ? `✍️ Concept-antwoord (in jouw stijl)${item.draft_created ? ' — staat als draft in de thread' : ' — nog niet in Outlook'}`
                        : '✍️ Jouw antwoord'}
                    </Typography>
                    {editConcept?.itemId !== item.id && item.concept && (
                      <Stack direction="row" spacing={0} alignItems="center" sx={{ ml: 'auto' }}>
                        {isInternalAddr(item.from_email) && (
                          <Tooltip title="Verstuur dit concept direct (alleen interne adressen) en archiveer de originele mail — sneltoets Z">
                            <Button size="small" variant="contained" disabled={actionBusy === 'send'}
                              onClick={() => sendConcept(item)}
                              startIcon={actionBusy === 'send' ? <CircularProgress size={12} color="inherit" /> : <SendIcon sx={{ fontSize: 14 }} />}
                              sx={{ mr: 0.5, py: 0, px: 1, fontSize: 12, textTransform: 'none', fontWeight: 700 }}>
                              Verstuur & archiveer
                            </Button>
                          </Tooltip>
                        )}
                        {!item.draft_created && (
                          <Tooltip title="Zet dit concept als reply-draft in je Outlook-thread — versturen blijft aan jou">
                            <Button size="small" variant="outlined" disabled={pushingDraft === item.id}
                              onClick={() => pushToOutlook(item)}
                              sx={{ mr: 0.5, py: 0, px: 1, fontSize: 12, textTransform: 'none' }}>
                              {pushingDraft === item.id ? 'Bezig…' : '📥 Zet in Outlook'}
                            </Button>
                          </Tooltip>
                        )}
                        <Tooltip title="Concept aanpassen (ververst ook de Outlook-draft als die er staat)">
                          <IconButton size="small" onClick={() => setEditConcept({ itemId: item.id, text: item.concept })}>
                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Concept verwijderen — ook uit je Outlook-concepten">
                          <IconButton size="small" onClick={() => deleteConcept(item)}>
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </Stack>
                  {item.draft_created && conceptOpen[item.id] && (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 0.75 }}>
                      💡 Staat ook in je Outlook-app onder <b>Concepten</b> — de "open"-link opent de webversie.
                    </Typography>
                  )}
                  {/* Expliciet tonen naar wie het antwoord gaat (reply-all: afzender + Aan, minus jij, plus CC). */}
                  {(() => {
                    const seen = new Set();
                    const dedup = (arr) => (arr || []).filter((a) => {
                      const k = String(a || '').toLowerCase();
                      if (!k || isMe(a) || seen.has(k)) return false;
                      seen.add(k); return true;
                    });
                    const to = dedup([item.from_email, ...(item.to_recipients || [])]);
                    const cc = dedup(item.cc_recipients || []);
                    const total = to.length + cc.length;
                    if (total === 0) return null;
                    return (
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                        <Tooltip title="Frits antwoordt standaard aan iedereen in Aan + CC (Allen), zodat er niemand uit de thread valt.">
                          <Chip size="small" label={total > 1 ? `↩ Allen · ${total}` : '↩ Alleen afzender'}
                            sx={{ height: 20, fontSize: 11, fontWeight: 800, bgcolor: total > 1 ? '#e0e7ff' : '#f1f5f9', color: total > 1 ? '#4338ca' : '#475569' }} />
                        </Tooltip>
                        <Typography variant="caption" color="text.secondary">
                          Aan: {addrList(to, 4)}{cc.length > 0 && <> · CC: {addrList(cc, 4)}</>}
                        </Typography>
                      </Stack>
                    );
                  })()}
                  {editConcept?.itemId === item.id ? (
                    <>
                      <TextField fullWidth multiline minRows={4} maxRows={14} size="small" value={editConcept.text}
                        autoFocus
                        onChange={(e) => setEditConcept((ec) => ({ ...ec, text: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') { e.preventDefault(); setEditConcept(null); }
                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); saveConcept(item); }
                        }}
                        helperText="Esc = annuleren · ⌘/Ctrl+Enter = opslaan als draft"
                        sx={{ bgcolor: '#fff' }} />
                      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                        <Button size="small" onClick={() => setEditConcept(null)} sx={{ textTransform: 'none' }}>Annuleren</Button>
                        <Button size="small" variant="contained" disabled={editConcept.saving || !editConcept.text.trim()}
                          startIcon={editConcept.saving ? <CircularProgress size={13} color="inherit" /> : null}
                          onClick={() => saveConcept(item)} sx={{ textTransform: 'none', fontWeight: 700 }}>
                          {item.concept ? 'Opslaan & draft verversen' : 'Als draft in Outlook zetten'}
                        </Button>
                        {isInternalAddr(item.from_email) && (
                          <Tooltip title="Interne mail: opslaan en meteen versturen (server checkt dat álle ontvangers intern zijn)">
                            <span>
                              <Button size="small" variant="contained" color="success"
                                disabled={editConcept.saving || !editConcept.text.trim() || actionBusy === 'send'}
                                startIcon={actionBusy === 'send' ? <CircularProgress size={13} color="inherit" /> : <SendIcon sx={{ fontSize: 15 }} />}
                                onClick={() => saveAndSend(item)} sx={{ textTransform: 'none', fontWeight: 800 }}>
                                Opslaan & versturen
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </>
                  ) : (
                    <Box onClick={() => setConceptOpen((c) => ({ ...c, [item.id]: !c[item.id] }))} sx={{ cursor: 'pointer' }}
                      title={conceptOpen[item.id] ? 'Inklappen' : 'Volledig concept tonen'}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6,
                        ...(conceptOpen[item.id] ? {} : { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }) }}>
                        {item.concept}
                      </Typography>
                      {!conceptOpen[item.id] && (
                        <Typography variant="caption" sx={{ color: '#4338ca', fontWeight: 700 }}>▾ toon alles</Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* Actie-suggesties — direct uitvoerbaar (draft-only waar het mail betreft) */}
              {(item.suggested_actions || []).length > 0 && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                  {item.suggested_actions.map((a, i) => {
                    const cfg = ACTION_BTN[a.type];
                    if (!cfg) {
                      return (
                        <Tooltip key={i} title="Deze actie komt in een volgende fase">
                          <Chip size="small" label={`💡 ${a.label || a.type}`} variant="outlined" sx={{ color: '#64748b' }} />
                        </Tooltip>
                      );
                    }
                    return (
                      <Button key={i} variant="outlined" size="small" startIcon={actionBusy === a.type ? <CircularProgress size={13} /> : cfg.icon}
                        disabled={!!actionBusy} onClick={(e) => cfg.run(item, a, e)}
                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 5 }}>
                        {a.label || cfg.fallback}
                      </Button>
                    );
                  })}
                </Stack>
              )}

              {/* Offerte als bijlage — voor offerte/beantwoorden-mails */}
              {(item.category === 'offerte' || item.category === 'beantwoorden' || item.project_match?.project_number) && (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                  <TextField size="small" label="Offertenr." placeholder={item.project_match?.project_number || 'bv. 9572'}
                    value={attachOffer.itemId === item.id ? attachOffer.number : ''}
                    onChange={(e) => setAttachOffer({ itemId: item.id, number: e.target.value, busy: false })}
                    sx={{ width: 130 }} />
                  <Button size="small" variant="outlined" disabled={attachOffer.busy && attachOffer.itemId === item.id}
                    startIcon={attachOffer.busy && attachOffer.itemId === item.id ? <CircularProgress size={13} /> : <span>📎</span>}
                    onClick={() => doAttachOffer(item)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 5 }}>
                    Offerte als bijlage
                  </Button>
                  <Button size="small" variant="outlined" disabled={scope.busy && scope.itemId === item.id}
                    startIcon={scope.busy && scope.itemId === item.id ? <CircularProgress size={13} /> : <span>🔍</span>}
                    onClick={() => doScopeCheck(item)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 5 }}>
                    Scope-check tegen offerte
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<span>✏️</span>}
                    onClick={() => openOfferteAanpassen(item)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 5 }}>
                    Offerte aanpassen
                  </Button>
                  {scope.itemId === item.id && scope.result && (
                    scope.result.error ? (
                      <Alert severity="error" sx={{ width: '100%', mt: 0.5 }}>{scope.result.error}</Alert>
                    ) : (
                      <Alert severity={scope.result.scope_change ? (scope.result.risico === 'hoog' ? 'error' : 'warning') : 'success'}
                        sx={{ width: '100%', mt: 0.5 }} onClose={() => setScope({ itemId: null, busy: false, result: null })}>
                        {scope.result.scope_change ? (
                          <>
                            <b>Let op — mogelijke scope-wijziging</b> (risico: {scope.result.risico}). {scope.result.toelichting}
                            {(scope.result.punten || []).length > 0 && (
                              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                {scope.result.punten.map((p, i) => <li key={i}><Typography variant="caption">{p}</Typography></li>)}
                              </Box>
                            )}
                          </>
                        ) : <>✅ De vraag lijkt binnen de offerte-scope te vallen.</>}
                      </Alert>
                    )
                  )}
                </Stack>
              )}

              {/* Wie kan dit oppakken? — Slimme toewijzing inline. Alleen bij delegeerbaar
                  TEAMWERK (triage: taak_voor='team'); persoonlijke/administratieve taken niet.
                  Oudere items zonder taak_voor: alleen tonen als er een project aan hangt. */}
              {(item.category === 'taak' || item.category === 'planning')
                && (item.taak_voor ? item.taak_voor === 'team' : !!item.project_match?.project_number) && (
                <Box sx={{ mt: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <TextField select size="small" label="Functie" value={assign.role}
                      onChange={(e) => setAssign((a) => ({ ...a, role: e.target.value, candidates: null }))} sx={{ width: 150 }}>
                      {['Editor', 'Animator', 'Illustrator', 'Camera', 'Geluid', 'Motion designer', 'Regie', 'Producer'].map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                    </TextField>
                    <Button size="small" variant="outlined"
                      startIcon={assign.loading && assign.itemId === item.id ? <CircularProgress size={13} /> : <span>🧑‍🤝‍🧑</span>}
                      onClick={() => runAssign(item)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 5 }}>
                      Wie kan dit oppakken?
                    </Button>
                  </Stack>
                  {assign.itemId === item.id && assign.candidates && (
                    assign.candidates.length === 0 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>Geen kandidaten met deze functie.</Typography>
                    ) : (
                      <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                        {assign.candidates.map((c, i) => {
                          const pct = c.capacityPercentage;
                          const col = pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#16a34a';
                          return (
                            <Stack key={c.id} direction="row" alignItems="center" spacing={1} sx={{ fontSize: 13 }}>
                              {i === 0 && <Chip size="small" label="meeste ruimte" sx={{ height: 16, fontSize: 9, bgcolor: '#dcfce7', color: '#166534' }} />}
                              <Typography variant="caption" sx={{ fontWeight: i === 0 ? 800 : 600, flex: 1 }}>{c.name || c.firstName}</Typography>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: c.availableHours > 0 ? '#16a34a' : '#ef4444' }}>
                                {c.availableHours > 0 ? `${c.availableHours}u vrij` : `${Math.abs(c.availableHours)}u óver`}
                              </Typography>
                              <Typography variant="caption" sx={{ color: col, fontWeight: 700, minWidth: 40, textAlign: 'right' }}>{pct}%</Typography>
                            </Stack>
                          );
                        })}
                        <Typography variant="caption" color="text.disabled">Komende 2 weken · voorstel; je wijst zelf toe.</Typography>
                      </Stack>
                    )
                  )}
                </Box>
              )}


              {actionMsg?.itemId === item.id && (
                <Alert severity="success" sx={{ mt: 1.5 }} onClose={() => setActionMsg(null)}>
                  {actionMsg.text}
                  {actionMsg.link && (
                    <> — <Link href={actionMsg.link} target="_blank" rel="noopener" sx={{ fontWeight: 700 }}>open concept</Link></>
                  )}
                </Alert>
              )}

              {/* 💬 Inline chat met Frits — direct vragen, geen popup. Kent de hele thread.
                  Losse component met lokale input → typen re-rendert de lijst niet. */}
              {(() => {
                const c = chatFor(item);
                return (
                  <InlineChat
                    key={item.id}
                    item={item}
                    messages={c.messages}
                    busy={c.busy}
                    onSend={sendChat}
                    onApplyConcept={applyChatConcept}
                    inputRef={chatInputRef}
                  />
                );
              })()}
            </Box>

            {/* Acties — max 6 contextuele knoppen, de rest achter "Meer" (sneltoets M).
                Afhandel-rij (Klaar/Skip/Archief/Naar map) blijft rechts staan. */}
            <Box sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 1.5, sm: 2 }, borderTop: '1px solid #f1f5f9', bgcolor: '#fafbfc' }}>
              <Stack direction="row" spacing={{ xs: 0.25, sm: 1 }} flexWrap="wrap" useFlexGap alignItems="center">
                {(() => {
                  const acts = [
                    { key: 'bekijk', show: true, icon: <VisibilityOutlinedIcon />, label: isMobile ? 'Mail' : 'Bekijk mail',
                      tip: 'Originele mail bekijken (B)', onClick: () => openPreview(item) },
                    { key: 'antwoord', show: !item.concept && editConcept?.itemId !== item.id, icon: <EditOutlinedIcon />,
                      label: isMobile ? 'Antwoord' : 'Antwoord schrijven', tip: 'Zelf een antwoord schrijven — komt als draft in de Outlook-thread (E)',
                      onClick: () => setEditConcept({ itemId: item.id, text: '' }) },
                    { key: 'factuur', show: looksLikeInvoice(item), icon: <FactCheckOutlinedIcon />, label: 'Factuur-check',
                      tip: 'PDF-factuur checken tegen de Gripp-projectregels (inkoop verwacht)', onClick: () => runInvoiceCheck(item) },
                    { key: 'naar-factuur', show: looksLikeInvoice(item),
                      icon: invCheck?.forwarding ? <CircularProgress size={13} /> : <ForwardToInboxIcon />,
                      label: 'Naar factuur@',
                      tip: 'Doorsturen naar factuur@fullframe.nl + archiveren — gebeurt pas als jij hier klikt',
                      disabled: !!invCheck?.forwarding, onClick: () => forwardToFactuur(item) },
                    { key: 'opgepakt', show: !!followUpTarget(item),
                      icon: actionBusy === 'followup' ? <CircularProgress size={13} /> : <TaskAltIcon />, label: 'Opgepakt?',
                      tip: `Heeft ${(followUpTarget(item) || '').split('@')[0].split('.')[0]} hier al op gereageerd? Zo niet: reminder-concept (O)`,
                      disabled: !!actionBusy, onClick: () => followUp(item) },
                    { key: 'todo', show: true, icon: <AddTaskIcon />, label: 'Todo',
                      tip: 'Zet deze mail als todo in je eigen Microsoft To-Do-lijst (T)',
                      onClick: () => openTaskDlg(item, (item.suggested_actions || []).find((a) => a.type === 'todo') || null, 'todo') },
                    { key: 'delegeren', show: true, icon: <ForwardToInboxIcon />, label: 'Delegeren',
                      tip: 'Mail doorsturen naar een collega — met check of die vandaag kan (D)', onClick: () => openDelegateDlg(item) },
                    { key: 'extern', show: isInternalAddr(item.from_email), icon: <SendIcon />,
                      label: isMobile ? 'Extern' : 'Extern antwoorden',
                      tip: 'Antwoord het oorspronkelijke (externe) contact van deze geforwarde thread — Frits schrijft een nieuw concept',
                      onClick: () => openExternDlg(item) },
                    { key: 'leer', show: true, icon: <SchoolOutlinedIcon />, label: 'Leer Frits',
                      tip: 'Klopt de inschatting niet? Leer het Frits — vanaf de volgende run toegepast (L)',
                      onClick: () => setLessonDlg({ item, choice: null, text: '' }) },
                    { key: 'outlook', show: !!item.web_link, icon: <OpenInNewIcon />,
                      label: isMobile ? 'Outlook' : 'Open in Outlook', tip: item.draft_created ? 'Open in Outlook (concept staat in de thread)' : 'Open in Outlook',
                      href: item.web_link },
                  ].filter((a) => a.show);
                  const visible = acts.slice(0, 6);
                  const overflow = acts.slice(6);
                  return (
                    <>
                      {visible.map((a) => (
                        <Tooltip key={a.key} title={a.tip || ''}>
                          <span>
                            <Button variant="text" size="small" startIcon={a.icon} disabled={a.disabled}
                              onClick={a.onClick} href={a.href} target={a.href ? '_blank' : undefined} rel={a.href ? 'noopener' : undefined}
                              sx={{ textTransform: 'none', fontWeight: 700, color: 'text.secondary' }}>
                              {a.label}
                            </Button>
                          </span>
                        </Tooltip>
                      ))}
                      {overflow.length > 0 && (
                        <Button ref={meerBtnRef} variant="text" size="small" startIcon={<MoreHorizIcon />}
                          onClick={(e) => setMeerAnchor(e.currentTarget)}
                          sx={{ textTransform: 'none', fontWeight: 700, color: 'text.secondary' }}>
                          Meer
                        </Button>
                      )}
                      <Menu anchorEl={meerAnchor} open={!!meerAnchor} onClose={() => setMeerAnchor(null)}>
                        {overflow.map((a) => (
                          <MenuItem key={a.key} disabled={a.disabled}
                            onClick={() => {
                              setMeerAnchor(null);
                              if (a.href) window.open(a.href, '_blank', 'noopener');
                              else a.onClick?.();
                            }}>
                            <ListItemIcon>{a.icon}</ListItemIcon>
                            <ListItemText primaryTypographyProps={{ fontSize: 14 }}>{a.label}</ListItemText>
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  );
                })()}
                <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
                {statusFilter === 'open' && !isMobile && (
                  <>
                    {(item.concept || item.draft_created) && isInternalAddr(item.from_email) && (
                      <Tooltip title="Concept direct versturen + originele mail archiveren — kan alleen naar interne adressen (fullframe/full-frame), sneltoets Z">
                        <Button variant="outlined" size="small" color="primary" onClick={() => sendConcept(item)}
                          disabled={actionBusy === 'send'}
                          startIcon={actionBusy === 'send' ? <CircularProgress size={14} /> : <SendIcon />}
                          sx={{ textTransform: 'none', fontWeight: 700 }}>
                          Verstuur & klaar
                        </Button>
                      </Tooltip>
                    )}
                    <Tooltip title="Mail naar een Outlook-map — optioneel voortaan automatisch (sneltoets V)">
                      <Button variant="text" size="small" startIcon={<DriveFileMoveOutlinedIcon />} onClick={() => openMoveDlg(item)}
                        sx={{ textTransform: 'none', color: 'text.secondary' }}>
                        Naar map
                      </Button>
                    </Tooltip>
                    <Tooltip title="Mail naar je Outlook-archief + afronden in Frits (sneltoets A)">
                      <Button variant="text" size="small" startIcon={<ArchiveOutlinedIcon />} onClick={() => archive(item)}
                        sx={{ textTransform: 'none', color: 'text.secondary' }}>
                        Archiveren
                      </Button>
                    </Tooltip>
                    <Tooltip title="Mail naar de Outlook-prullenbak + afronden (sneltoets X of Delete)">
                      <Button variant="text" size="small" startIcon={<DeleteOutlineIcon />} onClick={() => verwijderMail(item)}
                        sx={{ textTransform: 'none', color: 'text.secondary' }}>
                        Verwijderen
                      </Button>
                    </Tooltip>
                    <Button variant="text" size="small" startIcon={<SkipNextIcon />} onClick={() => handle(item, 'skipped')}
                      sx={{ textTransform: 'none', color: 'text.secondary' }}>
                      Overslaan
                    </Button>
                    <Button variant="contained" size="small" color="success" startIcon={<CheckIcon />} onClick={() => handle(item, 'done')}
                      sx={{ textTransform: 'none', fontWeight: 800 }}>
                      Klaar & volgende
                    </Button>
                  </>
                )}
              </Stack>
              {statusFilter === 'open' && isMobile && (
                <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
                  {(item.concept || item.draft_created) && isInternalAddr(item.from_email) && (
                    <Button variant="outlined" color="primary" onClick={() => sendConcept(item)} aria-label="Verstuur concept (intern)"
                      disabled={actionBusy === 'send'}
                      sx={{ minWidth: 52, minHeight: 46, px: 0, borderColor: '#e2e8f0' }}>
                      {actionBusy === 'send' ? <CircularProgress size={18} /> : <SendIcon />}
                    </Button>
                  )}
                  <Button variant="outlined" onClick={() => openMoveDlg(item)} aria-label="Naar map"
                    sx={{ minWidth: 52, minHeight: 46, px: 0, color: 'text.secondary', borderColor: '#e2e8f0' }}>
                    <DriveFileMoveOutlinedIcon />
                  </Button>
                  <Button variant="outlined" onClick={() => archive(item)} aria-label="Archiveren"
                    sx={{ minWidth: 52, minHeight: 46, px: 0, color: 'text.secondary', borderColor: '#e2e8f0' }}>
                    <ArchiveOutlinedIcon />
                  </Button>
                  <Button variant="outlined" onClick={() => handle(item, 'skipped')} aria-label="Overslaan"
                    sx={{ minWidth: 52, minHeight: 46, px: 0, color: 'text.secondary', borderColor: '#e2e8f0' }}>
                    <SkipNextIcon />
                  </Button>
                  <Button variant="contained" color="success" startIcon={<CheckIcon />} onClick={() => handle(item, 'done')}
                    sx={{ textTransform: 'none', fontWeight: 800, flex: 1, minHeight: 46 }}>
                    Klaar & volgende
                  </Button>
                </Stack>
              )}
            </Box>
          </Paper>

          {/* Mini-voortgang onder de kaart */}
          <Box sx={{ mt: 1.5, height: 4, borderRadius: 2, bgcolor: '#f1f4f5', overflow: 'hidden' }}>
            <Box sx={{ width: `${((idx) / Math.max(items.length, 1)) * 100}%`, height: '100%', bgcolor: '#526075', transition: 'width .3s' }} />
          </Box>
        </>
      )}

      {/* Factuur-check resultaat */}
      <Dialog open={!!invCheck} onClose={() => !invCheck?.loading && setInvCheck(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 800 }}>🧾 Factuur-check</DialogTitle>
        <DialogContent dividers>
          {invCheck?.loading ? (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
              <CircularProgress size={26} />
              <Typography variant="body2" color="text.secondary">PDF lezen → opdrachtnummer zoeken → Gripp-regels vergelijken…</Typography>
            </Stack>
          ) : invCheck?.result && (
            <Stack spacing={1.5}>
              <Alert severity={{ match: 'success', afwijking: 'warning' }[invCheck.result.verdict] || 'info'}>
                <b>{{ match: 'Klopt met Gripp', afwijking: 'Wijkt af van Gripp', geen_pdf: 'Geen PDF gevonden',
                     geen_opdrachtnummer: 'Geen opdrachtnummer op de factuur', project_niet_gevonden: 'Project niet gevonden',
                     onduidelijk: 'Niet eenduidig', fout: 'Check mislukt' }[invCheck.result.verdict] || invCheck.result.verdict}</b>
                {' — '}{invCheck.result.toelichting}
              </Alert>
              {invCheck.result.invoice && (
                <Typography variant="body2" color="text.secondary">
                  {invCheck.result.invoice.leverancier || 'Leverancier onbekend'}
                  {invCheck.result.invoice.factuurnummer ? ` · factuur ${invCheck.result.invoice.factuurnummer}` : ''}
                  {invCheck.result.invoice.bedrag_excl != null ? ` · €${Number(invCheck.result.invoice.bedrag_excl).toLocaleString('nl-NL')} excl.` : ''}
                  {invCheck.result.project ? ` · opdracht ${invCheck.result.project.nummer} (${invCheck.result.project.naam})` : ''}
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvCheck(null)} disabled={invCheck?.loading} sx={{ textTransform: 'none' }}>Sluiten</Button>
          {invCheck?.result && !invCheck.loading && (
            <Button variant="contained" onClick={() => forwardToFactuur(invCheck.item)} disabled={invCheck?.forwarding}
              startIcon={invCheck?.forwarding ? <CircularProgress size={14} color="inherit" /> : <ForwardToInboxIcon />}
              sx={{ textTransform: 'none', fontWeight: 800 }}>
              Doorsturen naar factuur@ & archiveren
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Project koppelen */}
      <Dialog open={!!linkDlg} onClose={() => setLinkDlg(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 800 }}>📁 Mail aan project koppelen</DialogTitle>
        <DialogContent dividers>
          {linkDlg && !linkDlg.item?.project_match && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Ik zie nog geen gekoppeld project voor <b>{linkDlg.item?.from_name || linkDlg.item?.from_email}</b>.
              Kies hieronder alsnog een project — ik onthoud het dan voor deze mailthread én voor deze contactpersoon.
            </Alert>
          )}
          <Autocomplete
            options={linkDlg?.opts || []} value={null} blurOnSelect clearOnBlur autoHighlight openOnFocus
            loading={linkDlg?.loading} filterOptions={(x) => x}
            getOptionLabel={(o) => `${o.project_number || o.offer_number || ''} — ${o.project_name}`}
            isOptionEqualToValue={(o, v) => (o.airtable_project_id || o.offer_number) === (v.airtable_project_id || v.offer_number)}
            onInputChange={(_e, val, reason) => { if (reason === 'input') { setLinkDlg((d) => (d ? { ...d, q: val } : d)); searchProjects(val); } }}
            onChange={(_e, val) => { if (val && linkDlg) linkProject(linkDlg.item, val); }}
            renderOption={(props, o) => (
              <li {...props} key={o.airtable_project_id || `offer-${o.offer_number}`}>
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {o.kind === 'offerte' && <span style={{ background: '#eef2ff', color: '#4338ca', fontSize: 10, fontWeight: 800, padding: '1px 5px', borderRadius: 4, marginRight: 6 }}>OFFERTE</span>}
                    {o.project_name}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{[o.project_number || o.offer_number, o.klant_name, o.contactpersoon_name, o.status].filter(Boolean).join(' · ')}</div>
                </div>
              </li>
            )}
            renderInput={(params) => <TextField {...params} autoFocus label="Zoek project of offerte" placeholder="Naam, nummer, klant of contactpersoon…" />}
            noOptionsText={linkDlg?.q?.length >= 2 ? 'Geen projecten of offertes gevonden' : 'Typ om te zoeken'} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDlg(null)} sx={{ textTransform: 'none' }}>Annuleren</Button>
        </DialogActions>
      </Dialog>

      {/* ↩️ Undo-toast (zoals Outlook): laatste afhandeling terugdraaien. De stapel
          blijft bestaan na de toast — ← of ⌘Z werkt dus ook later nog. */}
      <Snackbar
        open={undoToast && !!lastAction}
        autoHideDuration={8000}
        onClose={(_e, reason) => { if (reason !== 'clickaway') setUndoToast(false); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={
          lastAction ? ({ done: '✔ Afgehandeld', skipped: '⏭ Overgeslagen', archive: '🗃 Gearchiveerd' }[lastAction.type] || 'Afgehandeld')
            + ` — ${(lastAction.item?.subject || '').slice(0, 40)}` : ''
        }
        action={
          <Button size="small" onClick={undoLast} disabled={undoBusy}
            startIcon={undoBusy ? <CircularProgress size={12} color="inherit" /> : null}
            sx={{ color: '#93c5fd', fontWeight: 800, textTransform: 'none' }}>
            Ongedaan maken (← / ⌘Z)
          </Button>
        }
        ContentProps={{ sx: { bgcolor: '#1e293b', fontWeight: 600 } }}
      />

      {/* Onboarding — eerste bezoek + via het ?-icoon in de header */}
      <FritsOnboarding open={showOnboarding} onClose={() => { markOnboardingSeen(); setShowOnboarding(false); }} />

      {/* Eigen e-mailhandtekening (rich paste — opmaak + afbeeldingen blijven staan) */}
      <SignatureDialog open={showSignature} onClose={() => setShowSignature(false)} />
      <ToneProfileDialog open={showTone} onClose={() => setShowTone(false)} />

      {/* Sneltoetsen-overzicht (?) */}
      <Dialog open={showShortcuts} onClose={() => setShowShortcuts(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>⌨️ Sneltoetsen</DialogTitle>
        <DialogContent dividers>
          {[['Enter', 'Klaar & volgende'], ['S', 'Overslaan (tijdelijk — komt terug bij volgende run)'], ['A', 'Archiveren (mail écht naar archief)'],
            ['X', 'Verwijderen (mail naar prullenbak)'],
            ['B', 'Bekijk originele mail'], ['E', 'Antwoord bewerken / schrijven'], ['V', 'Verplaats naar map'],
            ['C', 'Chat met Frits (focus op het chatveld)'], ['M', 'Meer-menu openen'],
            ['D', 'Delegeren aan collega'], ['T', 'Taak aanmaken'], ['L', 'Leer Frits'],
            ['O', 'Opgepakt? — reactie-check + evt. reminder-concept'],
            ['Z', 'Verstuur concept & klaar (alleen interne ontvangers)'],
            ['← / ⌘Z', 'Terug — laatste afhandeling ongedaan maken (meerdere keren mogelijk)'],
            ['Esc', 'Popup of editor sluiten'], ['⌘/Ctrl+Enter', 'In editor: opslaan als draft'],
          ].map(([key, label]) => (
            <Stack key={key} direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.5 }}>
              <Chip label={key} size="small" sx={{ fontFamily: 'monospace', fontWeight: 700, minWidth: 96 }} />
              <Typography variant="body2">{label}</Typography>
            </Stack>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowShortcuts(false)} sx={{ textTransform: 'none' }}>Sluiten (Esc)</Button>
        </DialogActions>
      </Dialog>

      {/* Vaste actie — naar map óf automatisch doorsturen (bv. bonnetjes → factuur@) */}
      <Dialog open={!!moveDlg} onClose={() => !moveDlg?.busy && setMoveDlg(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 800 }}>📁 Vaste actie</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <ToggleButtonGroup size="small" exclusive value={moveDlg?.mode || 'map'}
              onChange={(_e, v) => v && setMoveDlg((d) => ({ ...d, mode: v }))}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, px: 1.5 } }}>
              <ToggleButton value="map">Naar map</ToggleButton>
              <ToggleButton value="forward">Doorsturen naar adres</ToggleButton>
            </ToggleButtonGroup>
            {moveDlg?.mode === 'forward' ? (
              <>
                <TextField fullWidth size="small" label="Doorsturen naar" placeholder="factuur@fullframe.nl"
                  value={moveDlg?.forwardTo || ''} autoFocus
                  onChange={(e) => setMoveDlg((d) => ({ ...d, forwardTo: e.target.value }))} />
                <FormControlLabel control={<Checkbox checked={!!moveDlg?.thenArchive}
                  onChange={(e) => setMoveDlg((d) => ({ ...d, thenArchive: e.target.checked }))} />}
                  label={<Typography variant="body2">Daarna archiveren (mail uit je inbox)</Typography>} />
              </>
            ) : (
              <Autocomplete size="small" options={folders || []} loading={folders === null}
                getOptionLabel={(f) => f.name || ''} value={moveDlg?.folder || null}
                onChange={(_e, v) => setMoveDlg((d) => ({ ...d, folder: v }))}
                renderInput={(params) => <TextField {...params} label="Outlook-map" autoFocus />} />
            )}
            <FormControlLabel control={<Checkbox checked={!!moveDlg?.remember}
              onChange={(e) => setMoveDlg((d) => ({ ...d, remember: e.target.checked }))} />}
              label={<Typography variant="body2">
                Voortaan automatisch voor alle mails van <b>{moveDlg?.item?.from_email}</b>
              </Typography>} />
            <Typography variant="caption" color="text.secondary">
              Automatische acties draaien bij elke run (07:30 + "Check nu") en worden gelogd onder "Afgehandeld",
              zodat je altijd kunt terugzien wat er gebeurd is.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveDlg(null)} disabled={moveDlg?.busy} sx={{ textTransform: 'none' }}>Annuleren</Button>
          <Button variant="contained" onClick={submitMove}
            disabled={moveDlg?.busy || (moveDlg?.mode === 'forward' ? !(moveDlg?.forwardTo || '').includes('@') : !moveDlg?.folder)}
            startIcon={moveDlg?.busy ? <CircularProgress size={14} color="inherit" /> : <DriveFileMoveOutlinedIcon />}
            sx={{ textTransform: 'none', fontWeight: 800 }}>
            {moveDlg?.mode === 'forward' ? 'Doorsturen' : 'Verplaatsen'}{moveDlg?.remember ? ' & onthouden' : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Taak aanmaken — bewerkbare titel + toelichting, voor jezelf of een collega */}
      <Dialog open={!!taskDlg} onClose={() => !taskDlg?.busy && setTaskDlg(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 800 }}>✅ Taak aanmaken</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField fullWidth size="small" label="Taak" value={taskDlg?.name || ''}
              onChange={(e) => setTaskDlg((d) => ({ ...d, name: e.target.value }))} />
            <TextField fullWidth size="small" multiline minRows={3} label="Toelichting (komt in de taak-notities)"
              value={taskDlg?.notes || ''}
              onChange={(e) => setTaskDlg((d) => ({ ...d, notes: e.target.value }))} />
            <Autocomplete size="small" options={colleagues || []} loading={colleagues === null}
              getOptionLabel={(c) => c.name || ''} value={taskDlg?.assignee || null}
              onChange={(_e, v) => { setTaskDlg((d) => ({ ...d, assignee: v, today: null })); if (v) checkToday(v.name, setTaskDlg); }}
              renderInput={(params) => <TextField {...params} label="Voor wie? (leeg = voor jezelf)" />} />
            {taskDlg?.assignee && taskDlg?.today && !taskDlg.today.loading && (
              <Alert severity={taskDlg.today.busy ? 'warning' : 'success'} sx={{ py: 0.25 }}>
                {taskDlg.today.busy
                  ? `${taskDlg.assignee.name.split(' ')[0]} heeft vandaag: ${taskDlg.today.items.map((i) => `${i.kind} — ${i.name}`).join(' · ')}`
                  : `Geen draaidag of vrije dag gevonden voor ${taskDlg.assignee.name.split(' ')[0]} vandaag.`}
              </Alert>
            )}
            <ToggleButtonGroup size="small" exclusive value={taskDlg?.destination || 'todo'}
              onChange={(_e, v) => v && setTaskDlg((d) => ({ ...d, destination: v }))}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, px: 1.5 } }}>
              <ToggleButton value="airtable">Airtable (team)</ToggleButton>
              <ToggleButton value="todo">Microsoft To-Do</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskDlg(null)} disabled={taskDlg?.busy} sx={{ textTransform: 'none' }}>Annuleren</Button>
          <Button variant="contained" onClick={submitTask} disabled={taskDlg?.busy || !(taskDlg?.name || '').trim()}
            startIcon={taskDlg?.busy ? <CircularProgress size={14} color="inherit" /> : <AddTaskIcon />}
            sx={{ textTransform: 'none', fontWeight: 800 }}>
            Taak aanmaken
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delegeren — mail doorsturen naar een collega, met vandaag-check */}
      <Dialog open={!!delegateDlg} onClose={() => !delegateDlg?.busy && setDelegateDlg(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 800 }}>📨 Delegeren aan een collega</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Autocomplete size="small" options={colleagues || []} loading={colleagues === null}
              getOptionLabel={(c) => c.name || ''} value={delegateDlg?.colleague || null}
              onChange={(_e, v) => {
                setDelegateDlg((d) => ({
                  ...d, colleague: v, today: null,
                  comment: v ? d.comment.replace(/^Hoi [^,]*,/, `Hoi ${v.name.split(' ')[0]},`) : d.comment,
                }));
                if (v) checkToday(v.name, setDelegateDlg);
              }}
              renderInput={(params) => <TextField {...params} label="Collega" />} />
            {delegateDlg?.colleague && delegateDlg?.today && !delegateDlg.today.loading && (
              <Alert severity={delegateDlg.today.busy ? 'warning' : 'success'} sx={{ py: 0.25 }}>
                {delegateDlg.today.busy
                  ? `Let op: ${delegateDlg.colleague.name.split(' ')[0]} heeft vandaag ${delegateDlg.today.items.map((i) => `${i.kind} — ${i.name}`).join(' · ')} — beloof niet zomaar 'vandaag'.`
                  : `${delegateDlg.colleague.name.split(' ')[0]} heeft vandaag geen draaidag of vrije dag in de planning.`}
              </Alert>
            )}
            <TextField fullWidth size="small" multiline minRows={4} label="Bericht (de originele mail gaat er automatisch onder mee)"
              value={delegateDlg?.comment || ''}
              onChange={(e) => setDelegateDlg((d) => ({ ...d, comment: e.target.value }))} />
            <FormControlLabel control={<Checkbox checked={!!delegateDlg?.makeTask}
              onChange={(e) => setDelegateDlg((d) => ({ ...d, makeTask: e.target.checked }))} />}
              label={<Typography variant="body2">Ook een Airtable-taak aanmaken voor deze collega</Typography>} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          <Button onClick={() => setDelegateDlg(null)} disabled={delegateDlg?.busy} sx={{ textTransform: 'none' }}>Annuleren</Button>
          <Box sx={{ flex: 1 }} />
          <Button variant="outlined" onClick={() => submitDelegate(false)} disabled={delegateDlg?.busy || !delegateDlg?.colleague}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            Als concept
          </Button>
          <Button variant="contained" onClick={() => submitDelegate(true)} disabled={delegateDlg?.busy || !delegateDlg?.colleague}
            startIcon={delegateDlg?.busy ? <CircularProgress size={14} color="inherit" /> : <ForwardToInboxIcon />}
            sx={{ textTransform: 'none', fontWeight: 800 }}>
            Direct doorsturen
          </Button>
        </DialogActions>
      </Dialog>

      {/* "Leer Frits" — feedback wordt direct een triage-regel */}
      <Dialog open={!!lessonDlg} onClose={() => !lessonDlg?.busy && setLessonDlg(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 800 }}>🎓 Leer Frits</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Wat klopte er niet aan deze inschatting? Frits onthoudt het en past het toe op alle volgende mails.
          </Typography>
          <Stack spacing={0.75}>
            {LESSON_CHOICES.map((c) => (
              <Button key={c.key} variant={lessonDlg?.choice === c.key ? 'contained' : 'outlined'} size="small"
                onClick={() => setLessonDlg((d) => ({ ...d, choice: d.choice === c.key ? null : c.key }))}
                sx={{ textTransform: 'none', fontWeight: 600, justifyContent: 'flex-start' }}>
                {c.label}
              </Button>
            ))}
          </Stack>
          <TextField fullWidth size="small" multiline minRows={2} sx={{ mt: 1.5 }}
            label="Eigen toelichting (optioneel — of schrijf hier je eigen regel)"
            value={lessonDlg?.text || ''}
            onChange={(e) => setLessonDlg((d) => ({ ...d, text: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLessonDlg(null)} disabled={lessonDlg?.busy} sx={{ textTransform: 'none' }}>Annuleren</Button>
          <Button variant="contained" onClick={submitLesson}
            disabled={lessonDlg?.busy || (!lessonDlg?.choice && !(lessonDlg?.text || '').trim())}
            startIcon={lessonDlg?.busy ? <CircularProgress size={14} color="inherit" /> : <SchoolOutlinedIcon />}
            sx={{ textTransform: 'none', fontWeight: 800 }}>
            Frits laten onthouden
          </Button>
        </DialogActions>
      </Dialog>

      {/* Originele mail (preview) */}
      <Dialog open={!!preview?.open} onClose={() => setPreview(null)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 800, pr: 5 }}>{preview?.subject || 'Mail'}</DialogTitle>
        <DialogContent dividers>
          {/* Van / Aan / CC — de context die je in de mail-app ook zou zien */}
          {!preview?.loading && (
            <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #eef2f6' }}>
              <Typography variant="caption" sx={{ display: 'block' }}>
                <b>Van:</b> {preview?.from_name || preview?.item?.from_name} &lt;{preview?.from_email || preview?.item?.from_email}&gt;
              </Typography>
              {(preview?.to?.length > 0 || preview?.item?.to_recipients?.length > 0) && (
                <Typography variant="caption" sx={{ display: 'block' }}>
                  <b>Aan:</b> {(preview?.to || preview?.item?.to_recipients || []).join(', ')}
                </Typography>
              )}
              {(preview?.cc?.length > 0 || preview?.item?.cc_recipients?.length > 0) && (
                <Typography variant="caption" sx={{ display: 'block' }}>
                  <b>CC:</b> {(preview?.cc || preview?.item?.cc_recipients || []).join(', ')}
                  {(preview?.item?.cc_recipients || []).some(isMe) && !(preview?.item?.to_recipients || []).some(isMe) ? '  — jij staat in CC' : ''}
                </Typography>
              )}
            </Box>
          )}
          {preview?.loading
            ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>
            : <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{renderMailText(preview?.body)}</Typography>}
          {!preview?.loading && preview?.myReply && (
            <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, color: '#166534', mb: 0.5 }}>
                ✅ Jouw antwoord ({(preview.myReply.at || '').slice(0, 10)})
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                {renderMailText(preview.myReply.body)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)} sx={{ textTransform: 'none' }}>Sluiten</Button>
        </DialogActions>
      </Dialog>

      {/* ✉️ Extern antwoorden — kandidaat kiezen, concept laten schrijven, draft openen */}
      <Dialog open={!!externDlg} onClose={() => externDlg?.busy || setExternDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>✉️ Antwoord het externe contact</DialogTitle>
        <DialogContent dividers>
          {externDlg?.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>
          ) : externDlg?.result ? (
            <>
              <Alert severity="success" sx={{ mb: 1.5 }}>
                Concept staat als nieuwe mail aan <b>{externDlg.result.to_email}</b> in je Outlook-concepten.
                {externDlg.result.web_link && (
                  <> <Link href={externDlg.result.web_link} target="_blank" rel="noopener" sx={{ fontWeight: 700 }}>Open in Outlook</Link></>
                )}
              </Alert>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1.5 }}>
                {externDlg.result.concept}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Frits schrijft een níeuwe mail aan het oorspronkelijke contact (niet een reply aan de collega),
                met de interne afstemming als context. Versturen doe je zelf vanuit Outlook.
              </Typography>
              {(externDlg?.candidates || []).length > 0 && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                  {externDlg.candidates.map((c) => (
                    <Chip key={c} size="small" label={c} clickable
                      onClick={() => setExternDlg((d) => ({ ...d, to: c }))}
                      sx={{ fontWeight: 700, bgcolor: externDlg.to === c ? '#e0e7ff' : undefined }} />
                  ))}
                </Stack>
              )}
              <TextField fullWidth size="small" label="Aan (extern adres)" value={externDlg?.to || ''}
                onChange={(e) => setExternDlg((d) => ({ ...d, to: e.target.value }))} sx={{ mb: 1.5 }} />
              <TextField fullWidth size="small" multiline minRows={2} label="Instructie (optioneel)"
                placeholder="bv. stel dinsdag 1 sep 12:30 voor als lunchmoment, met 7 of 14 sep als alternatief"
                value={externDlg?.instructie || ''}
                onChange={(e) => setExternDlg((d) => ({ ...d, instructie: e.target.value }))} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExternDlg(null)} disabled={externDlg?.busy} sx={{ textTransform: 'none' }}>Sluiten</Button>
          {!externDlg?.result && (
            <Button variant="contained" onClick={maakExternConcept}
              disabled={externDlg?.busy || !(externDlg?.to || '').includes('@')}
              startIcon={externDlg?.busy ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              Maak concept-draft
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Visuele agenda-weergave */}
      <Dialog open={visualOpen} onClose={() => setVisualOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 800 }}>📅 Agenda-weergave — {avail.date} · {avail.start}–{avail.end}</DialogTitle>
        <DialogContent dividers>
          {/* Deelnemers zijn bewerkbaar: weghalen (✕) of een collega toevoegen —
              de check draait dan direct opnieuw met de nieuwe lijst (30 aug). */}
          {(() => {
            const availItem = (items || []).find((i) => i.id === avail.itemId);
            if (!availItem) return null;
            const lijst = (availPeople?.itemId === avail.itemId && availPeople.lijst) || [];
            const herdraai = (nieuweLijst) => {
              setAvailPeople({ itemId: avail.itemId, lijst: nieuweLijst });
              runAvailability(availItem, { attendees: nieuweLijst });
            };
            return (
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                {lijst.map((e) => (
                  <Chip key={e} size="small" label={e.split('@')[0]}
                    onDelete={lijst.length > 1 ? () => herdraai(lijst.filter((x) => x !== e)) : undefined}
                    sx={{ fontWeight: 700 }} />
                ))}
                <Autocomplete size="small" sx={{ width: 200 }} value={null} blurOnSelect clearOnBlur
                  options={(colleagues || []).filter((c) => c.email && !lijst.includes(String(c.email).toLowerCase()))}
                  getOptionLabel={(o) => o?.name || o?.email || ''}
                  onOpen={() => loadColleagues()}
                  onChange={(_e, c) => { if (c?.email) herdraai([...lijst, String(c.email).toLowerCase()]); }}
                  renderInput={(params) => <TextField {...params} placeholder="+ collega toevoegen" variant="standard" />} />
                {avail.loading && <CircularProgress size={16} />}
              </Stack>
            );
          })()}
          {avail.result && !avail.result.error && (() => {
            const res = avail.result;
            const reqS = toMin(avail.start), reqE = toMin(avail.end);
            const v = res.verdict || {};
            const sev = v.all_free ? { c: '#dcfce7', t: '#15803d', label: '✅ Iedereen beschikbaar' }
              : v.external_block ? { c: '#fee2e2', t: '#b91c1c', label: '⛔ Niet mogelijk — extern conflict of draaidag' }
              : v.only_internal_conflicts ? { c: '#fef3c7', t: '#92400e', label: '⚠️ Kan — mits interne afspraken verschuiven' }
              : { c: '#f1f5f9', t: '#475569', label: 'Geen agenda-data' };
            const hours = [];
            for (let h = 8; h <= 18; h++) hours.push(h);
            return (
              <Box>
                <Box sx={{ px: 1.25, py: 0.75, borderRadius: 1, bgcolor: sev.c, color: sev.t, fontWeight: 800, fontSize: 13, mb: 1.5 }}>{sev.label}</Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Box sx={{ width: 96 }} />
                  <Box sx={{ display: 'flex', flex: 1 }}>
                    {hours.map((h) => <Box key={h} sx={{ flex: 1, fontSize: 10, color: '#94a3b8' }}>{h}:00</Box>)}
                  </Box>
                </Box>
                <Stack spacing={1}>
                  {(res.attendees || []).map((att) => {
                    const { events: laid, lanes } = laneLayout(att.events || []);
                    const rowH = Math.max(30, lanes * 26);
                    return (
                      <Box key={att.email} sx={{ display: 'flex', alignItems: 'stretch' }}>
                        <Box sx={{ width: 96, pr: 1, pt: 0.5, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{att.email.split('@')[0]}</Box>
                        <Box sx={{ position: 'relative', flex: 1, height: rowH, bgcolor: '#f8fafc', borderRadius: 0.5, overflow: 'hidden', border: '1px solid #eef2f7' }}>
                          <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${pct(reqS)}%`, width: `${pct(reqE) - pct(reqS)}%`, bgcolor: 'rgba(15,118,110,0.10)', borderLeft: '2px solid #0f766e', borderRight: '2px solid #0f766e', zIndex: 0 }} />
                          {!att.accessible && (
                            <Box title="agenda niet inzichtelijk" sx={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(45deg,#e2e8f0,#e2e8f0 6px,#f1f5f9 6px,#f1f5f9 12px)' }} />
                          )}
                          {laid.map((e, i) => {
                            const col = CONFLICT_COLORS[e.kind] || '#94a3b8';
                            const left = pct(e.s), width = Math.max(1.5, pct(e.e2) - pct(e.s));
                            const label = (e.all_day ? '' : `${e.start} `) + e.subject;
                            return (
                              <Tooltip key={i} title={`${e.all_day ? 'hele dag' : `${e.start}–${e.end}`} · ${e.subject}${e.conflict ? ' — botst met het gevraagde slot' : ''}`}>
                                <Box sx={{
                                  position: 'absolute', left: `${left}%`, width: `${width}%`, top: e.lane * 26 + 2, height: 22,
                                  bgcolor: e.conflict ? col : '#fff', border: e.conflict ? `1px solid ${col}` : `1px solid ${col}55`,
                                  color: e.conflict ? '#fff' : '#334155', borderRadius: 0.5, px: 0.6, fontSize: 10.5,
                                  fontWeight: e.conflict ? 700 : 500, display: 'flex', alignItems: 'center',
                                  overflow: 'hidden', whiteSpace: 'nowrap', opacity: e.conflict ? 1 : 0.92, zIndex: 1,
                                }}>{label}</Box>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
                <Stack direction="row" spacing={1.5} sx={{ mt: 1.5, flexWrap: 'wrap' }} useFlexGap>
                  {[['#0f766e', 'gevraagd'], ['#f59e0b', 'intern'], ['#ef4444', 'extern'], ['#7c3aed', 'draaidag']].map(([c, l]) => (
                    <Stack key={l} direction="row" spacing={0.5} alignItems="center">
                      <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: c }} />
                      <Typography variant="caption" color="text.secondary">{l}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button component="a" href={`https://outlook.office.com/calendar/view/day?date=${avail.date || ''}`}
            target="_blank" rel="noopener" sx={{ mr: 'auto', textTransform: 'none', fontWeight: 700 }}>
            🗓 Open mijn agenda
          </Button>
          <Button onClick={() => setVisualOpen(false)} sx={{ textTransform: 'none' }}>Sluiten</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
